import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import config from "./config";
import WelcomeScreen from "./WelcomeScreen";
import MessageInput from "./components/MessageInput";
import TypingIndicator from "./components/TypingIndicator";
import LocationMessage from "./components/LocationMessage";
import LinkPreview from "./components/LinkPreview";
import SideNavbar from "./components/SideNavbar";
import GroupAudioCall from "./components/GroupAudioCall";
import GroupVideoCall from "./components/GroupVideoCall";
import VoiceMessage from "./components/VoiceMessage";
import MessageContextMenu from "./components/MessageContextMenu";
import ForwardPicker from "./components/ForwardPicker";
import ContactMessage from "./components/ContactMessage";
import Contacts from "./components/Contacts";
import UserProfile from "./components/UserProfile";
import SharedContentModal from "./components/SharedContentModal";
import ImageViewer from "./components/ImageViewer";
import Status from "./components/Status";
import VibeUploadModal from "./components/VibeUploadModal";
import Groups from "./components/Groups";
import GroupMessages from "./components/GroupMessages";
import PinnedMessagesBar from "./components/PinnedMessagesBar";
import GroupCallJoinBar from "./components/GroupCallJoinBar";
import OngoingGroupCallCard from "./components/OngoingGroupCallCard";
import useActiveGroupCalls from "./hooks/useActiveGroupCalls";
import ViewOnceMedia from "./components/ViewOnceMedia";
import GroupMembersPage from "./components/GroupMembersPage";
import AdminLeaveGroupModal from "./components/AdminLeaveGroupModal";
import LeaveGroupChoiceModal from "./components/LeaveGroupChoiceModal";
import { isGroupAdmin } from "./utils/groupPermissions";
import {
  addToReadOnlyGroups,
  hideGroupFromList,
  loadHiddenGroups,
  loadReadOnlyGroups,
  removeFromReadOnlyGroups,
  saveReadOnlyGroups,
  unhideGroup,
} from "./utils/groupListStorage";
import {
  openImageViewer,
  closeImageViewer,
  getImageUrl,
} from "./utils/imageUtils";
import {
  formatFileSize,
  getDocumentIcon,
  getDocumentDownloadUrl,
} from "./utils/documentUtils";
import { getMediaUrl } from "./utils/mediaUtils";
import { extractUrls, renderTextWithLinks } from "./utils/linkify";
import Settings from "./components/Settings";
import StarredMessages from "./components/StarredMessages";
import ContactUs from "./components/ContactUs";
import Pricing from "./components/Pricing";
import PremiumBadge from "./components/PremiumBadge";
import usePremiumPlan from "./hooks/usePremiumPlan";
import AIChat from "./components/ai/AIChat";
import WallpaperPicker from "./components/WallpaperPicker";
import EmojiPicker from "./components/EmojiPicker";
import { getCurrentLocation } from "./utils/locationUtils";
import { formatLastSeen } from "./utils/lastSeen";
import {
  loadLockedChats,
  saveLockedChats,
  isChatLocked,
  hasChatLockPin,
  saveChatLockPin,
  verifyChatLockPin,
} from "./utils/chatLock";
import ChatLockModal, { ChatLockUnlockPanel } from "./components/ChatLockModal";
import ChatLockPinResetModal from "./components/ChatLockPinResetModal";
import "./styles/ChatLock.css";
import "./styles/Home.css";

function formatTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// Format a call duration (seconds) as h:mm:ss / m:ss for the in-chat call log.
function formatCallDuration(totalSeconds) {
  const secs = Math.max(0, parseInt(totalSeconds, 10) || 0);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatLastMessageTime(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = now - date;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    // Today - show time
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } else if (diffDays === 1) {
    // Yesterday
    return "Yesterday";
  } else if (diffDays < 7) {
    // This week - show day name
    return date.toLocaleDateString(undefined, { weekday: "short" });
  } else {
    // Older - show date
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  }
}

function getDateLabel(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = now.setHours(0, 0, 0, 0) - date.setHours(0, 0, 0, 0);
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7 && diffDays > 1) {
    return date.toLocaleDateString(undefined, { weekday: "long" });
  }
  return date.toLocaleDateString();
}

// Helper function to format dates
function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// Format relative time for seen status
function formatSeenTime(readAt) {
  if (!readAt) return "Seen";

  const now = new Date();
  const seenTime = new Date(readAt);

  // Handle invalid dates
  if (isNaN(seenTime.getTime())) return "Seen";

  const diffMs = now - seenTime;

  // Handle future dates (shouldn't happen but just in case)
  if (diffMs < 0) return "Seen just now";

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);

  // Less than 1 minute
  if (diffMinutes < 1) {
    return "Seen just now";
  }

  // 1 minute to 59 minutes
  if (diffMinutes < 60) {
    return `Seen ${diffMinutes}m ago`;
  }

  // 1 hour to 24 hours
  if (diffHours <= 24) {
    return `Seen ${diffHours}h ago`;
  }

  // 1 day
  if (diffDays === 1) {
    return "Seen 1d ago";
  }

  // 2-6 days
  if (diffDays < 7) {
    return `Seen ${diffDays}d ago`;
  }

  // 1 week
  if (diffWeeks === 1) {
    return "Seen 1w ago";
  }

  // More than 1 week - show full date
  const options = {
    month: 'short',
    day: 'numeric',
    year: seenTime.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  };
  return `Seen ${seenTime.toLocaleDateString('en-US', options)}`;
}

// Message status display: show text instead of ticks
function getTick(msg, messages, userId, currentUser, timeUpdateTrigger) {
  if (msg.from !== userId) return null; // Only show status for sent messages

  // Check if current user has read receipts enabled
  const readReceiptsEnabled =
    currentUser?.privacySettings?.readReceipts !== false;

  // Use the status field from the message object
  switch (msg.status) {
    case "sent":
      return <span className="message-status sent">Sent</span>;
    case "delivered":
      return <span className="message-status delivered">Delivered</span>;
    case "read":
      // Only show "Seen" if read receipts are enabled
      return readReceiptsEnabled ? (
        <span className="message-status read">{formatSeenTime(msg.readAt)}</span>
      ) : (
        <span className="message-status delivered">Delivered</span>
      );
    default:
      return <span className="message-status sent">Sent</span>;
  }
}

function Home({
  user,
  users,
  setUsers,
  to,
  setTo,
  selectedGroup,
  setSelectedGroup,
  messages,
  setMessages,
  text,
  setText,
  fetchMessages,
  sendMessage,
  handleVoiceMessage,
  handleGroupVoiceMessage,
  markMessagesAsDelivered,
  markMessagesAsRead,
  socket,
  refreshUsers,
  blockStatus,
}) {
  // Ensure messages is always an array to prevent runtime errors
  const safeMessages = Array.isArray(messages) ? messages : [];
  const isPremium = usePremiumPlan(user?.id);

  // State for showing group members page
  const [showGroupMembersPage, setShowGroupMembersPage] = useState(false);
  const [groupUpdateCallback, setGroupUpdateCallback] = useState(null);
  const [removeGroupFromListCallback, setRemoveGroupFromListCallback] = useState(null);

  // Debug logging to help identify the issue
  if (!Array.isArray(messages)) {
    console.warn(
      "Home component received non-array messages:",
      messages,
      typeof messages
    );
  }

  // Debug: Check if all required props are available
  // //console.log("Home component props:", {
  //   user: !!user,
  //   users: users?.length,
  //   to: !!to,
  //   setTo: typeof setTo === "function",
  //   messages: safeMessages?.length,
  //   setMessages: typeof setMessages === "function",
  //   text: !!text,
  //   setText: typeof setText === "function",
  //   fetchMessages: typeof fetchMessages === "function",
  //   sendMessage: typeof sendMessage === "function",
  //   handleVoiceMessage: typeof handleVoiceMessage === "function",
  //   handleGroupVoiceMessage: typeof handleGroupVoiceMessage === "function",
  //   markMessagesAsDelivered: typeof markMessagesAsDelivered === "function",
  //   markMessagesAsRead: typeof markMessagesAsRead === "function",
  //   socket: !!socket,
  // });

  const navigate = useNavigate();
  // const location = useLocation(); // Not used - using window.location instead
  
  // Get active tab from current route
  const activeTab = window.location.pathname.replace('/', '') || 'chats';
  const isWidePanelTab = activeTab === "pricing";
  
  // Get query params for selected user/group
  const searchParams = new URLSearchParams(window.location.search);
  const chatUserId = searchParams.get('chat');
  const groupId = searchParams.get('group');
  const highlightMessageId = searchParams.get('message');
  
  const [replyTo, setReplyTo] = useState(null);
  const [groupReplyTo, setGroupReplyTo] = useState(null);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [editMessage, setEditMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showChat, setShowChat] = useState(false);
  // UCMA AI assistant — shown inside the main chat pane (sidebar stays visible).
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [_isCallActive, setIsCallActive] = useState(false);
  const [showSidebarSearch, setShowSidebarSearch] = useState(false);
  const [contactMenu, setContactMenu] = useState({
    isVisible: false,
    position: { x: 0, y: 0 },
    contactId: null,
  });
  const [groupMenu, setGroupMenu] = useState({
    isVisible: false,
    position: { x: 0, y: 0 },
    groupId: null,
  });
  const [showChatSearch, setShowChatSearch] = useState(false);
  const [chatSearchTerm, setChatSearchTerm] = useState("");
  const [searchMatchIndex, setSearchMatchIndex] = useState(0);
  const [searchMatches, setSearchMatches] = useState([]);
  const [clearedChats, setClearedChats] = useState(() => {
    // Load cleared chats from localStorage
    try {
      const saved = localStorage.getItem("clearedChats");
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error("Error loading cleared chats:", error);
      return {};
    }
  });
  const [clearedGroups, setClearedGroups] = useState(() => {
    // Load cleared groups from localStorage
    try {
      const saved = localStorage.getItem("clearedGroups");
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error("Error loading cleared groups:", error);
      return {};
    }
  });
  const [blockedUsers, setBlockedUsers] = useState(() => {
    // Load blocked users from localStorage
    try {
      const saved = localStorage.getItem("blockedUsers");
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error("Error loading blocked users:", error);
      return {};
    }
  });
  const [lockedChats, setLockedChats] = useState(() => loadLockedChats());
  const [unlockedChats, setUnlockedChats] = useState({});
  const [chatLockModal, setChatLockModal] = useState({
    isVisible: false,
    mode: "setup",
    pendingContactId: null,
  });
  const [chatLockError, setChatLockError] = useState("");
  const [chatLockPinFailCount, setChatLockPinFailCount] = useState(0);
  const [showChatLockPinReset, setShowChatLockPinReset] = useState(false);
  const [readOnlyGroups, setReadOnlyGroups] = useState(() => loadReadOnlyGroups());
  const [hiddenGroups, setHiddenGroups] = useState(() => loadHiddenGroups());
  const [confirmModal, setConfirmModal] = useState({
    isVisible: false,
    title: "",
    message: "",
    onConfirm: null,
    confirmText: "Confirm",
    cancelText: "Cancel",
  });
  const [adminLeaveModal, setAdminLeaveModal] = useState({
    isVisible: false,
    groupId: null,
  });
  const [adminLeaveLoading, setAdminLeaveLoading] = useState(false);
  const [leaveGroupChoiceModal, setLeaveGroupChoiceModal] = useState({
    isVisible: false,
    groupId: null,
  });
  const [pendingLeaveOptions, setPendingLeaveOptions] = useState({
    deleteFromList: false,
  });
  const [leaveActionLoading, setLeaveActionLoading] = useState(false);
  const [activeGroupCall, setActiveGroupCall] = useState(null);
  const [groupCallJoinRequest, setGroupCallJoinRequest] = useState(null);
  const [joiningGroupCall, setJoiningGroupCall] = useState(false);
  const [joiningOngoingCallId, setJoiningOngoingCallId] = useState(null);
  const joinableGroupCalls = useActiveGroupCalls(user, socket);
  const [contextMenu, setContextMenu] = useState({
    isVisible: false,
    position: { x: 0, y: 0 },
    message: null,
  });
  const [forwardModal, setForwardModal] = useState({
    isVisible: false,
    message: null,
  });
  const [reactionDetailsMessageId, setReactionDetailsMessageId] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);

  // Call history state
  const [callHistory, setCallHistory] = useState(() => {
    try {
      const saved = localStorage.getItem("callHistory");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error("Error loading call history:", e);
      return [];
    }
  });

  // Wallpaper state - chat-specific
  const [showWallpaperPicker, setShowWallpaperPicker] = useState(false);
  const [chatWallpapers, setChatWallpapers] = useState(() => {
    // Load chat-specific wallpapers from localStorage
    const saved = localStorage.getItem("chatWallpapers");
    return saved ? JSON.parse(saved) : {};
  });

  // Get current chat wallpaper
  const getCurrentChatWallpaper = () => {
    if (to) {
      const wallpaper = chatWallpapers[to] || {
        id: "default",
        name: "Default",
        url: "#ffffff",
      };
      const result = {
        ...wallpaper,
        displaySettings: wallpaper.displaySettings || {
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        },
      };
      return result;
    } else if (selectedGroup) {
      const groupKey = `group:${selectedGroup._id}`;
      const wallpaper = chatWallpapers[groupKey] || {
        id: "default",
        name: "Default",
        url: "#ffffff",
      };
      const result = {
        ...wallpaper,
        displaySettings: wallpaper.displaySettings || {
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        },
      };
      return result;
    }
    return {
      id: "default",
      name: "Default",
      url: "#ffffff",
      displaySettings: {
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      },
    };
  };
  const [groupNotificationCount, setGroupNotificationCount] = useState(0);
  const [groupNotificationCounts, setGroupNotificationCounts] = useState({});
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showSharedContentModal, setShowSharedContentModal] = useState(false);
  const [sharedContentContactId, setSharedContentContactId] = useState(null);
  const [selectedUserForProfile, setSelectedUserForProfile] = useState(null);
  const [currentUserLocation, setCurrentUserLocation] = useState(null);
  // Store privacy-filtered profile data for currently selected chat user
  const [selectedUserProfileData, setSelectedUserProfileData] = useState(null);
  // Cache of privacy-filtered profile data for users in the sidebar list
  const [privacyProfiles, setPrivacyProfiles] = useState({});
  // Track per-user profile image loading status: undefined/true = loading, false = loaded, 'error' = failed
  const [profileImageLoading, setProfileImageLoading] = useState({});

  // Chats tab sub-tabs and vibe/status bubbles
  const [chatSubTab, setChatSubTab] = useState("recent"); // "recent" or "requests"
  const [vibeUsers, setVibeUsers] = useState([]); // contacts who have active statuses
  const [showVibeUploadModal, setShowVibeUploadModal] = useState(false);
  const [showVibeViewer, setShowVibeViewer] = useState(false);
  const [selectedVibeStatuses, setSelectedVibeStatuses] = useState([]);
  const [selectedVibeIndex, setSelectedVibeIndex] = useState(0);
  const [selectedVibeUser, setSelectedVibeUser] = useState(null);
  const [activeVibePanel, setActiveVibePanel] = useState(null);
  const [currentStatusViewers, setCurrentStatusViewers] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [statusLikes, setStatusLikes] = useState([]);
  const [commentLikes, setCommentLikes] = useState([]);
  const activeVibeStatusId = selectedVibeStatuses[selectedVibeIndex]?._id;

  // Typing indicator states
  const [typingUsers, setTypingUsers] = useState({}); // For individual chats
  const [groupTypingUsers, setGroupTypingUsers] = useState({}); // For group chats

  // Force re-render for updating relative times
  const [timeUpdateTrigger, setTimeUpdateTrigger] = useState(0);

  // Ref for chat window to enable auto-scrolling
  const chatWindowRef = useRef(null);
  const messagesEndRef = useRef(null);
  // Force stick-to-bottom after opening a chat (bypasses near-bottom check)
  const stickToBottomOnOpenRef = useRef(false);

  // Scroll chat window to the latest message (instant; retries after layout)
  const scrollToBottom = () => {
    const el = chatWindowRef.current;
    if (!el) return;

    const apply = () => {
      // behavior:'auto' overrides CSS scroll-behavior:smooth so we don't undershoot
      el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
    };

    apply();
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
  };
  
  // Handle URL changes for chat/group selection
  React.useEffect(() => {
    if (chatUserId && chatUserId !== to) {
      // Opening a normal chat closes the AI pane
      setAiChatOpen(false);
      // Fetch messages for the selected user
      fetchMessages(chatUserId);
      if (isMobile) {
        setShowChat(true);
      }
      // Clear group selection and leave group room
      if (selectedGroup) {
        if (socket) {
          socket.emit('leaveGroup', selectedGroup._id);
        }
        setSelectedGroup(null);
      }
      setTimeout(scrollToBottom, 200);
    } else if (groupId && (!selectedGroup || selectedGroup._id !== groupId)) {
      // Opening a group closes the AI pane
      setAiChatOpen(false);
      // Fetch and select the group
      const fetchGroup = async () => {
        try {
          // Leave previous group room if switching groups
          if (selectedGroup && selectedGroup._id !== groupId && socket) {
            socket.emit('leaveGroup', selectedGroup._id);
          }
          
          const response = await axios.get(`${config.apiUrl}/api/groups/${groupId}`);
          const newGroup = response.data;
          setSelectedGroup(newGroup);
          setTo(''); // Clear individual chat
          if (isMobile) {
            setShowChat(true);
          }
          
          // Join the new group room
          if (socket) {
            socket.emit('joinGroup', groupId);
          }
          
          // Prevent page scroll when group is opened - ensure header stays visible
          window.scrollTo(0, 0);
          
          // Mark messages as read for this group
          try {
            await axios.post(
              `${config.apiUrl}/api/groups/${groupId}/mark-read`,
              { userId: user.id }
            );
            
            // Clear notification count for this group
            setGroupNotificationCounts((prev) => {
              const newCounts = { ...prev };
              const clearedCount = newCounts[groupId] || 0;
              delete newCounts[groupId];
              setGroupNotificationCount((prev) => Math.max(0, prev - clearedCount));
              return newCounts;
            });
          } catch (error) {
            console.error("Error marking group messages as read:", error);
          }
        } catch (error) {
          console.error("Error fetching group:", error);
          // If group not found, navigate back to groups list
          navigate('/groups');
        }
      };
      
      fetchGroup();
    } else if (!chatUserId && !groupId) {
      // No chat selected, clear everything (keep AI pane open if active)
      if (to) {
        setTo('');
      }
      if (selectedGroup) {
        // Leave group room when clearing selection
        if (socket) {
          socket.emit('leaveGroup', selectedGroup._id);
        }
        setSelectedGroup(null);
      }
      if (isMobile && !aiChatOpen) {
        setShowChat(false);
      }
    }
  }, [chatUserId, groupId, window.location.pathname, aiChatOpen]);

  useEffect(() => {
    if (!highlightMessageId) return undefined;
    const delay = to || selectedGroup ? 400 : 900;
    const timer = window.setTimeout(() => {
      const el = document.querySelector(
        `[data-message-id="${highlightMessageId}"]`
      );
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("highlight-flash");
      window.setTimeout(() => el.classList.remove("highlight-flash"), 2000);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [highlightMessageId, to, selectedGroup, messages.length]);

  const canPinMessages = selectedGroup
    ? (selectedGroup.pinPermission || "admins") === "all" ||
      isGroupAdmin(selectedGroup, user?.id)
    : Boolean(to);

  const pinnedMessageIds = pinnedMessages.map((item) => String(item.messageId));

  const scrollToPinnedMessage = (messageId) => {
    const tryScroll = (attempts = 0) => {
      const el = document.querySelector(`[data-message-id="${messageId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("highlight-flash");
        window.setTimeout(() => el.classList.remove("highlight-flash"), 2000);
        return;
      }
      if (attempts < 6) {
        window.setTimeout(() => tryScroll(attempts + 1), 200);
      }
    };
    tryScroll();
  };

  useEffect(() => {
    if (!user?.id) {
      setPinnedMessages([]);
      return undefined;
    }

    let cancelled = false;

    const fetchPinned = async () => {
      try {
        if (to) {
          const response = await axios.get(
            `${config.apiUrl}/api/messages/pinned/${user.id}/${to}`
          );
          if (!cancelled) {
            setPinnedMessages(response.data?.pinnedMessages || []);
          }
          return;
        }
        if (selectedGroup?._id) {
          const response = await axios.get(
            `${config.apiUrl}/api/groups/${selectedGroup._id}/pinned`,
            { params: { userId: user.id } }
          );
          if (!cancelled) {
            setPinnedMessages(response.data?.pinnedMessages || []);
            if (response.data?.pinPermission && selectedGroup.pinPermission !== response.data.pinPermission) {
              setSelectedGroup((prev) =>
                prev ? { ...prev, pinPermission: response.data.pinPermission } : prev
              );
            }
          }
          return;
        }
        if (!cancelled) setPinnedMessages([]);
      } catch (error) {
        console.error("Error fetching pinned messages:", error);
        if (!cancelled) setPinnedMessages([]);
      }
    };

    fetchPinned();
    return () => {
      cancelled = true;
    };
  }, [to, selectedGroup?._id, user?.id]);

  useEffect(() => {
    if (!socket || !user?.id) return undefined;

    const handlePinnedMessagesUpdated = (data) => {
      if (!data) return;
      if (data.chatType === "direct" && to) {
        const chatId = [String(user.id), String(to)].sort().join("_");
        if (data.chatId === chatId) {
          setPinnedMessages(data.pinnedMessages || []);
        }
        return;
      }
      if (
        data.chatType === "group" &&
        selectedGroup &&
        String(data.groupId || data.chatId) === String(selectedGroup._id)
      ) {
        setPinnedMessages(data.pinnedMessages || []);
        if (data.pinPermission) {
          setSelectedGroup((prev) =>
            prev ? { ...prev, pinPermission: data.pinPermission } : prev
          );
        }
      }
    };

    socket.on("pinnedMessagesUpdated", handlePinnedMessagesUpdated);
    return () => {
      socket.off("pinnedMessagesUpdated", handlePinnedMessagesUpdated);
    };
  }, [socket, to, selectedGroup?._id, user?.id]);

  useEffect(() => {
    if (!selectedGroup?._id) {
      setActiveGroupCall(null);
      return undefined;
    }

    let cancelled = false;
    axios
      .get(`${config.apiUrl}/api/calls/group-active/${selectedGroup._id}`)
      .then((res) => {
        if (!cancelled && res.data?.groupCallId) setActiveGroupCall(res.data);
      })
      .catch(() => {
        if (!cancelled) setActiveGroupCall(null);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedGroup?._id]);

  useEffect(() => {
    if (!socket) return undefined;

    const matchesOpenGroup = (groupId) =>
      selectedGroup && String(groupId) === String(selectedGroup._id);

    const handleGroupCallActive = (data) => {
      if (!data?.groupCallId || !matchesOpenGroup(data.groupId)) return;
      setActiveGroupCall(data);
    };

    const handleGroupCallInactive = (data) => {
      if (!matchesOpenGroup(data?.groupId)) return;
      setActiveGroupCall((prev) => {
        if (!prev) return null;
        if (data?.groupCallId && String(prev.groupCallId) !== String(data.groupCallId)) {
          return prev;
        }
        return null;
      });
    };

    socket.on('groupCallActive', handleGroupCallActive);
    socket.on('groupCallInactive', handleGroupCallInactive);
    return () => {
      socket.off('groupCallActive', handleGroupCallActive);
      socket.off('groupCallInactive', handleGroupCallInactive);
    };
  }, [socket, selectedGroup?._id]);

  useEffect(() => {
    if (!socket) return undefined;
    const handleViewOnceUpdated = (data) => {
      if (!data?.messageId) return;
      setMessages((prev) => {
        if (!Array.isArray(prev)) return prev;
        return prev.map((msg) =>
          String(msg._id) === String(data.messageId)
            ? {
                ...msg,
                viewedBy: data.viewedBy || msg.viewedBy || [],
                viewOnceViewed:
                  String(data.openedBy) === String(user?.id) || msg.viewOnceViewed,
              }
            : msg
        );
      });
    };
    socket.on("messageViewOnceUpdated", handleViewOnceUpdated);
    return () => socket.off("messageViewOnceUpdated", handleViewOnceUpdated);
  }, [socket, user?.id, setMessages]);
  
  const [imageViewer, setImageViewer] = useState({
    isOpen: false,
    imageUrl: "",
  });
  const handleGroupAvatarClick = (e) => {
    e.stopPropagation();
    if (selectedGroup?.avatar) {
      openImageViewer(selectedGroup.avatar, setImageViewer, config.apiUrl);
    }
  };

  // Listen for server notification when current user is removed from a group
  React.useEffect(() => {
    if (!socket || !user?.id) return;
    const handleRemovedFromGroup = (payload) => {
      const { groupId, groupName, groupAvatar } = payload || {};
      if (!groupId) return;
      const hidden = loadHiddenGroups();
      if (hidden[groupId]) {
        if (selectedGroup && selectedGroup._id === groupId) {
          setSelectedGroup(null);
          setShowGroupMembersPage(false);
        }
        return;
      }
      const next = addToReadOnlyGroups(groupId, {
        name: groupName || "Group",
        avatar: groupAvatar || null,
      });
      setReadOnlyGroups(next);
      // If viewing this group, keep it open; selectedGroup may still be in state
      if (selectedGroup && selectedGroup._id === groupId) {
        // Mark as read-only by removing membership locally
        setSelectedGroup((g) => (g ? { ...g, members: g.members || [] } : g));
      }
    };
    socket.on("removedFromGroup", handleRemovedFromGroup);
    return () => socket.off("removedFromGroup", handleRemovedFromGroup);
  }, [socket, user?.id, selectedGroup]);

  // Typing indicator listeners
  React.useEffect(() => {
    if (!socket || !user?.id) return;

    const handleTyping = (data) => {
      const { from, to, username, profileImage, isTyping } = data;

      // Only handle typing for current chat
      if (to !== user.id) return;

      setTypingUsers(prev => {
        const newTypingUsers = { ...prev };
        if (isTyping) {
          newTypingUsers[from] = { username, profileImage };
        } else {
          delete newTypingUsers[from];
        }
        return newTypingUsers;
      });
    };

    const handleGroupTyping = (data) => {
      const { groupId, userId, username, profileImage, isTyping } = data;

      // Only handle typing for current group
      if (!selectedGroup || selectedGroup._id !== groupId) return;

      // Don't show typing indicator for current user
      if (userId === user.id) return;

      setGroupTypingUsers(prev => {
        const newGroupTypingUsers = { ...prev };
        if (!newGroupTypingUsers[groupId]) {
          newGroupTypingUsers[groupId] = {};
        }

        if (isTyping) {
          newGroupTypingUsers[groupId][userId] = { username, profileImage };
        } else {
          delete newGroupTypingUsers[groupId][userId];

          // Clean up empty group objects
          if (Object.keys(newGroupTypingUsers[groupId]).length === 0) {
            delete newGroupTypingUsers[groupId];
          }
        }
        return newGroupTypingUsers;
      });
    };

    socket.on('userTyping', handleTyping);
    socket.on('groupUserTyping', handleGroupTyping);

    return () => {
      socket.off('userTyping', handleTyping);
      socket.off('groupUserTyping', handleGroupTyping);
    };
  }, [socket, user?.id, selectedGroup]);

  // Listen for new status/vibe uploads to refresh vibe bubbles
  React.useEffect(() => {
    if (!socket) return;
    const handleNewStatus = () => { fetchVibeUsers(); };
    const handleStatusDeleted = () => { fetchVibeUsers(); };
    const handleStatusViewUpdate = ({ statusId, viewer }) => {
      const viewerId = viewer.id?.toString?.() || getNestedUserId(viewer);
      updateVibeStatusState(statusId, (status) => {
        const ownerId = getVibeOwnerId(status, selectedVibeUser);
        if (ownerId && viewerId === ownerId) {
          return status;
        }
        const alreadyViewed = (status.views || []).some(
          (view) => getNestedUserId(view) === viewerId
        );
        if (alreadyViewed) {
          return status;
        }

        return {
          ...status,
          views: [...(status.views || []), { userId: viewer.id, viewedAt: viewer.viewedAt }],
        };
      });

      const ownerId =
        selectedVibeUser?.userId?.toString?.() || selectedVibeUser?.userId;
      if (
        activeVibePanel === "viewers" &&
        activeVibeStatusId === statusId &&
        ownerId &&
        viewerId !== ownerId &&
        !currentStatusViewers.some((entry) => getNestedUserId(entry) === viewerId)
      ) {
        setCurrentStatusViewers((prev) => [viewer, ...prev]);
      }
    };
    const handleStatusCommentUpdate = ({ statusId, comment }) => {
      updateVibeStatusState(statusId, (status) => ({
        ...status,
        comments: [...(status.comments || []), comment],
      }));

      if (activeVibePanel === "comments" && activeVibeStatusId === statusId) {
        setComments((prev) => [...prev, comment]);
      }
    };
    const handleCommentLikeUpdate = ({ statusId, commentId, likesCount }) => {
      if (activeVibeStatusId !== statusId) {
        return;
      }

      setComments((prev) =>
        prev.map((comment) =>
          comment._id === commentId ? { ...comment, likesCount } : comment
        )
      );
    };
    const handleStatusLikeUpdateGlobal = ({ statusId, action, likerId }) => {
      updateVibeStatusState(statusId, (status) => {
        const alreadyLiked = (status.likes || []).some(
          (like) => getNestedUserId(like) === likerId?.toString?.()
        );

        if (action === "liked" && !alreadyLiked) {
          return {
            ...status,
            likes: [...(status.likes || []), { userId: likerId, likedAt: new Date() }],
          };
        }

        if (action !== "liked" && alreadyLiked) {
          return {
            ...status,
            likes: (status.likes || []).filter(
              (like) => getNestedUserId(like) !== likerId?.toString?.()
            ),
          };
        }

        return status;
      });
    };
    socket.on("newStatusAvailable", handleNewStatus);
    socket.on("statusDeleted", handleStatusDeleted);
    socket.on("statusViewUpdate", handleStatusViewUpdate);
    socket.on("statusCommentUpdate", handleStatusCommentUpdate);
    socket.on("commentLikeUpdate", handleCommentLikeUpdate);
    socket.on("statusLikeUpdateGlobal", handleStatusLikeUpdateGlobal);
    return () => {
      socket.off("newStatusAvailable", handleNewStatus);
      socket.off("statusDeleted", handleStatusDeleted);
      socket.off("statusViewUpdate", handleStatusViewUpdate);
      socket.off("statusCommentUpdate", handleStatusCommentUpdate);
      socket.off("commentLikeUpdate", handleCommentLikeUpdate);
      socket.off("statusLikeUpdateGlobal", handleStatusLikeUpdateGlobal);
    };
  }, [socket, user?.id, activeVibePanel, activeVibeStatusId, currentStatusViewers, selectedVibeUser]);

  React.useEffect(() => {
    if (!showVibeViewer) {
      return;
    }

    resetVibeViewerPanels();
  }, [selectedVibeIndex, showVibeViewer]);

  // Update seen times periodically for dynamic relative time display
  React.useEffect(() => {
    // Update every 10 seconds for the first minute, then every minute
    const interval = setInterval(() => {
      // Trigger re-render to update relative times
      setTimeUpdateTrigger(prev => prev + 1);
    }, 10000); // Update every 10 seconds to catch "just now" transitions

    return () => clearInterval(interval);
  }, []);

  // If groups list refresh occurs (e.g., switching to Groups tab or re-fetch), ensure readOnly flag is cleared for re-joined groups
  React.useEffect(() => {
    // When selectedGroup updates and contains members including current user, clear readOnly flag
    if (selectedGroup && Array.isArray(selectedGroup.members)) {
      const isMember = selectedGroup.members.some((m) => (m.user?._id || m.user || m).toString?.() === user.id || ((m.user?._id || m.user) === user.id));
      if (isMember && readOnlyGroups[selectedGroup._id]) {
        setReadOnlyGroups((prev) => {
          const next = { ...prev };
          delete next[selectedGroup._id];
          try { localStorage.setItem("readOnlyGroups", JSON.stringify(next)); } catch { }
          return next;
        });
      }
    }
  }, [selectedGroup, user?.id]);

  // Wallpaper handlers - chat-specific
  const handleWallpaperSelect = (wallpaper) => {
    const key = to ? to : selectedGroup ? `group:${selectedGroup._id}` : null;
    if (!key) return;

    // If default wallpaper is selected, remove it from localStorage
    if (wallpaper.id === 'default' || wallpaper.isDefault) {
      const newChatWallpapers = { ...chatWallpapers };
      delete newChatWallpapers[key];
      setChatWallpapers(newChatWallpapers);

      try {
        localStorage.setItem(
          "chatWallpapers",
          JSON.stringify(newChatWallpapers)
        );
      } catch (error) {
        console.error("Error removing wallpaper:", error);
      }
      return;
    }

    const newChatWallpapers = {
      ...chatWallpapers,
      [key]: wallpaper,
    };
    setChatWallpapers(newChatWallpapers);

    try {
      localStorage.setItem(
        "chatWallpapers",
        JSON.stringify(newChatWallpapers)
      );
    } catch (error) {
      if (error.name === "QuotaExceededError") {
        // If localStorage is full, try to clear old wallpapers and retry
        console.warn(
          "localStorage quota exceeded, attempting to clear old wallpapers"
        );

        // Keep only the current chat wallpaper and remove others
        const minimalWallpapers = { [key]: wallpaper };
        try {
          localStorage.setItem(
            "chatWallpapers",
            JSON.stringify(minimalWallpapers)
          );
          setChatWallpapers(minimalWallpapers);
          alert(
            "Storage limit reached. Only the current chat wallpaper has been saved."
          );
        } catch (retryError) {
          console.error(
            "Failed to save wallpaper even after cleanup:",
            retryError
          );
          alert(
            "Unable to save wallpaper due to storage limitations. Please try a smaller image or clear browser data."
          );
        }
      } else {
        console.error("Error saving wallpaper:", error);
        alert("Failed to save wallpaper. Please try again.");
      }
    }
  };

  const handleWallpaperPickerToggle = () => {
    setShowWallpaperPicker(!showWallpaperPicker);
  };

  // Filter messages based on cleared status and search term
  const getFilteredMessages = () => {
    let filteredMessages = safeMessages;

    // First filter out old messages if chat was cleared (only show messages after clear timestamp)
    if (to && clearedChats[to]) {
      const clearTimestamp = clearedChats[to];
      // Only show messages that were created AFTER the clear timestamp
      filteredMessages = safeMessages.filter(msg => {
        const messageTime = new Date(msg.createdAt).getTime();
        return messageTime > clearTimestamp;
      });
    }

    // Filter out regular messages if user is blocked (but keep system messages)
    if (to && blockedUsers[to]) {
      filteredMessages = safeMessages.filter((msg) => {
        // Keep system messages (like block messages) and messages from blocked time or before
        if (
          msg.isSystemMessage ||
          msg.messageType === "block" ||
          msg.messageType === "unblock"
        ) {
          return true;
        }

        // Filter out messages from blocked user after block time
        if (msg.senderId === to && blockedUsers[to]) {
          const messageTime = new Date(msg.timestamp).getTime();
          const blockTime = blockedUsers[to].blockedAt;
          return messageTime <= blockTime;
        }

        return true;
      });
    }

    // Then apply search filter if there's a search term
    if (chatSearchTerm.trim()) {
      const searchTerm = chatSearchTerm.toLowerCase();
      filteredMessages = filteredMessages.filter((msg) => {
        // Search in text content
        if (msg.text && msg.text.toLowerCase().includes(searchTerm)) {
          return true;
        }

        // Search in media file names
        if (msg.imageUrl && msg.imageUrl.toLowerCase().includes(searchTerm)) {
          return true;
        }
        if (msg.videoUrl && msg.videoUrl.toLowerCase().includes(searchTerm)) {
          return true;
        }
        if (msg.audioName && msg.audioName.toLowerCase().includes(searchTerm)) {
          return true;
        }
        if (
          msg.documentName &&
          msg.documentName.toLowerCase().includes(searchTerm)
        ) {
          return true;
        }

        // Search in contact data
        if (
          msg.contactData &&
          msg.contactData.username &&
          msg.contactData.username.toLowerCase().includes(searchTerm)
        ) {
          return true;
        }

        // Search in location data
        if (
          msg.locationData &&
          msg.locationData.address &&
          msg.locationData.address.toLowerCase().includes(searchTerm)
        ) {
          return true;
        }

        return false;
      });
    }

    return filteredMessages;
  };

  // Get filtered messages for display
  const displayMessages = getFilteredMessages();
  const reactionDetailsMessage = reactionDetailsMessageId
    ? safeMessages.find((m) => m._id === reactionDetailsMessageId)
    : null;

  // Group messages by date label
  const grouped = {};
  displayMessages.forEach((msg) => {
    const label = getDateLabel(msg.createdAt);
    if (!grouped[label]) grouped[label] = [];
    grouped[label].push(msg);
  });
  const labels = Object.keys(grouped);

  const handleSend = () => {
    if (!text.trim()) return;
    sendMessage({ replyTo: replyTo?._id });
    setText("");
    setReplyTo(null);
    // Scroll to bottom after sending a message
    setTimeout(scrollToBottom, 50);
  };

  const handleMediaSend = async (mediaFiles, options = {}) => {
    if (!to || mediaFiles.length === 0) return;

    // Create FormData with all media files
    const formData = new FormData();

    // Append all media files to the same FormData
    mediaFiles.forEach((file) => {
      formData.append("media", file);
    });

    formData.append("from", user.id);
    formData.append("to", to);

    if (replyTo) {
      formData.append("replyTo", replyTo._id);
    }
    if (options.viewOnce) {
      formData.append("viewOnce", "true");
    }

    try {
      // Send all media files in a single request
      // Messages will be received in real-time via Socket.IO
      const response = await fetch(`${config.apiUrl}/api/messages/media`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to send media files");
      }

      const result = await response.json();
      // //console.log(
      //   `${result.count} media files sent successfully via real-time Socket.IO`
      // );

      // Clear reply state after successful send
      setReplyTo(null);
    } catch (error) {
      console.error("Error sending media files:", error);
      alert("Failed to send media files. Please try again.");
    }
  };

  const handleDocumentSend = async (documentFiles) => {
    if (!to || documentFiles.length === 0) return;

    // Create FormData with all document files
    const formData = new FormData();

    // Append all document files to the same FormData
    documentFiles.forEach((file) => {
      formData.append("documents", file);
    });

    formData.append("from", user.id);
    formData.append("to", to);

    if (replyTo) {
      formData.append("replyTo", replyTo._id);
    }

    try {
      // Send all document files in a single request
      // Messages will be received in real-time via Socket.IO
      const response = await fetch(`${config.apiUrl}/api/messages/documents`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to send documents");
      }

      const result = await response.json();
      // //console.log(
      //   `${result.count} documents sent successfully via real-time Socket.IO`
      // );

      // Clear reply state after successful send
      setReplyTo(null);

      // Return success to indicate completion
      return result;
    } catch (error) {
      console.error("Error sending documents:", error);
      alert("Failed to send documents. Please try again.");
      throw error; // Re-throw to let the caller handle it
    }
  };

  const handleAudioSend = async (audioFiles, options = {}) => {
    if (!to || audioFiles.length === 0) return;

    // Create FormData with all audio files
    const formData = new FormData();

    // Append all audio files to the same FormData
    audioFiles.forEach((file) => {
      formData.append("audio", file);
    });

    formData.append("from", user.id);
    formData.append("to", to);

    if (replyTo) {
      formData.append("replyTo", replyTo._id);
    }
    if (options.viewOnce) {
      formData.append("viewOnce", "true");
    }

    try {
      // Send all audio files in a single request
      // Messages will be received in real-time via Socket.IO
      const response = await fetch(`${config.apiUrl}/api/messages/audio`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to send audio files");
      }

      const result = await response.json();
      // //console.log(
      //   `${result.count} audio files sent successfully via real-time Socket.IO`
      // );

      // Clear reply state after successful send
      setReplyTo(null);

      // Return success to indicate completion
      return result;
    } catch (error) {
      console.error("Error sending audio files:", error);
      alert("Failed to send audio files. Please try again.");
      throw error; // Re-throw to let the caller handle it
    }
  };

  const handleContactSend = async (contact) => {
    if (!to || !contact) return;

    try {
      // Send contact via API
      const response = await fetch(
        `${config.apiUrl}/api/messages/send-contact`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            from: user.id,
            to: to,
            contactId: contact.id,
            replyTo: replyTo?._id,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send contact");
      }

      const result = await response.json();
      //console.log("Contact sent successfully via real-time Socket.IO");

      // Clear reply state after successful send
      setReplyTo(null);

      // Return success to indicate completion
      return result;
    } catch (error) {
      console.error("Error sending contact:", error);
      alert("Failed to send contact. Please try again.");
      throw error; // Re-throw to let the caller handle it
    }
  };

  const handleLocationSend = async (locationData) => {
    if (!to || !locationData) return;

    try {
      const messageData = {
        from: user.id,
        to: to,
        text: `📍 ${locationData.address}`,
        messageType: "location",
        locationData: locationData,
        replyTo: replyTo?._id || null,
      };

      //console.log("Sending location message:", messageData);

      // Send location message via Socket.IO
      socket.emit("sendMessage", messageData);

      // Clear reply state after successful send
      setReplyTo(null);

      //console.log("Location sent successfully via Socket.IO");
    } catch (error) {
      console.error("Error sending location:", error);
      alert("Failed to send location. Please try again.");
      throw error;
    }
  };

  // Instant GIF send — emit remote URL over socket (no download/re-upload)
  const handleGifSend = (gifUrl) => {
    if (!gifUrl || !socket) return;

    // GIF images load async and grow scrollHeight — retry scroll after send
    const scrollAfterGif = () => {
      [0, 50, 150, 350, 600].forEach((ms) => setTimeout(scrollToBottom, ms));
    };

    if (to) {
      socket.emit("sendMessage", {
        from: user.id,
        to,
        text: "",
        messageType: "image",
        imageUrl: gifUrl,
        replyTo: replyTo?._id || null,
      });
      setReplyTo(null);
      scrollAfterGif();
      return;
    }

    if (selectedGroup) {
      socket.emit("sendGroupMessage", {
        from: user.id,
        groupId: selectedGroup._id,
        text: "",
        messageType: "image",
        imageUrl: gifUrl,
        replyTo: groupReplyTo?._id || null,
      });
      setGroupReplyTo(null);
      scrollAfterGif();
    }
  };

  // Group attachment handlers
  const handleGroupMediaSend = async (mediaFiles, options = {}) => {
    if (!selectedGroup || mediaFiles.length === 0) return;

    // Create FormData with all media files
    const formData = new FormData();

    // Append all media files to the same FormData
    mediaFiles.forEach((file) => {
      formData.append("media", file);
    });

    formData.append("from", user.id);
    formData.append("groupId", selectedGroup._id);

    if (groupReplyTo) {
      formData.append("replyTo", groupReplyTo._id);
    }
    if (options.viewOnce) {
      formData.append("viewOnce", "true");
    }

    try {
      // Send all media files in a single request
      // Messages will be received in real-time via Socket.IO
      const response = await fetch(
        `${config.apiUrl}/api/groups/${selectedGroup._id}/media`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send media files");
      }

      const result = await response.json();
      // //console.log(
      //   `${result.count} media files sent successfully to group via real-time Socket.IO`
      // );

      // Clear reply state after successful send
      setGroupReplyTo(null);
    } catch (error) {
      console.error("Error sending group media files:", error);
      alert("Failed to send media files. Please try again.");
    }
  };

  const handleGroupDocumentSend = async (documentFiles) => {
    if (!selectedGroup || documentFiles.length === 0) return;

    // Create FormData with all document files
    const formData = new FormData();

    // Append all document files to the same FormData
    documentFiles.forEach((file) => {
      formData.append("documents", file);
    });

    formData.append("from", user.id);
    formData.append("groupId", selectedGroup._id);

    if (groupReplyTo) {
      formData.append("replyTo", groupReplyTo._id);
    }

    try {
      // Send all document files in a single request
      // Messages will be received in real-time via Socket.IO
      const response = await fetch(
        `${config.apiUrl}/api/groups/${selectedGroup._id}/documents`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send documents");
      }

      const result = await response.json();
      // //console.log(
      //   `${result.count} documents sent successfully to group via real-time Socket.IO`
      // );

      // Clear reply state after successful send
      setGroupReplyTo(null);

      // Return success to indicate completion
      return result;
    } catch (error) {
      console.error("Error sending group documents:", error);
      alert("Failed to send documents. Please try again.");
      throw error; // Re-throw to let the caller handle it
    }
  };

  const handleGroupAudioSend = async (audioFiles, options = {}) => {
    if (!selectedGroup || audioFiles.length === 0) return;

    // Create FormData with all audio files
    const formData = new FormData();

    // Append all audio files to the same FormData
    audioFiles.forEach((file) => {
      formData.append("audio", file);
    });

    formData.append("from", user.id);
    formData.append("groupId", selectedGroup._id);

    if (groupReplyTo) {
      formData.append("replyTo", groupReplyTo._id);
    }
    if (options.viewOnce) {
      formData.append("viewOnce", "true");
    }

    try {
      // Send all audio files in a single request
      // Messages will be received in real-time via Socket.IO
      const response = await fetch(
        `${config.apiUrl}/api/groups/${selectedGroup._id}/audio`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send audio files");
      }

      const result = await response.json();
      // //console.log(
      //   `${result.count} audio files sent successfully to group via real-time Socket.IO`
      // );

      // Clear reply state after successful send
      setGroupReplyTo(null);

      // Return success to indicate completion
      return result;
    } catch (error) {
      console.error("Error sending group audio files:", error);
      alert("Failed to send audio files. Please try again.");
      throw error; // Re-throw to let the caller handle it
    }
  };

  const handleGroupContactSend = async (contact) => {
    if (!selectedGroup || !contact) return;

    try {
      // Send contact via API
      const response = await fetch(
        `${config.apiUrl}/api/groups/${selectedGroup._id}/send-contact`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            from: user.id,
            groupId: selectedGroup._id,
            contactId: contact.id,
            replyTo: groupReplyTo?._id,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send contact");
      }

      const result = await response.json();
      //console.log("Contact sent successfully to group via real-time Socket.IO");

      // Clear reply state after successful send
      setGroupReplyTo(null);

      // Return success to indicate completion
      return result;
    } catch (error) {
      console.error("Error sending group contact:", error);
      alert("Failed to send contact. Please try again.");
      throw error; // Re-throw to let the caller handle it
    }
  };

  const handleGroupLocationSend = async (locationData) => {
    if (!selectedGroup || !locationData) return;

    try {
      const messageData = {
        from: user.id,
        groupId: selectedGroup._id,
        text: `📍 ${locationData.address}`,
        messageType: "location",
        locationData: locationData,
        replyTo: groupReplyTo?._id || null,
      };

      //console.log("Sending group location message:", messageData);

      // Send location message via Socket.IO
      socket.emit("sendGroupMessage", messageData);

      // Clear reply state after successful send
      setGroupReplyTo(null);

      //console.log("Location sent successfully to group via Socket.IO");
    } catch (error) {
      console.error("Error sending group location:", error);
      alert("Failed to send location. Please try again.");
      throw error;
    }
  };

  // Keep the legacy image send function for backward compatibility
  const handleImageSend = async (images) => {
    if (!to || images.length === 0) return;

    // Create FormData with all images
    const formData = new FormData();

    // Append all images to the same FormData
    images.forEach((image) => {
      formData.append("images", image);
    });

    formData.append("from", user.id);
    formData.append("to", to);

    if (replyTo) {
      formData.append("replyTo", replyTo._id);
    }

    try {
      // Send all images in a single request
      const response = await fetch(`${config.apiUrl}/api/messages/image`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to send images");
      }

      const result = await response.json();
      //console.log(`${result.count} images sent successfully:`, result.messages);
    } catch (error) {
      console.error("Error sending images:", error);
      alert("Failed to send images. Please try again.");
    }

    setReplyTo(null);
  };

  // Filter users based on search term
  const filteredUsers = users.filter((u) =>
    u.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Prefetch privacy-aware profile images for users in the visible filtered list
  React.useEffect(() => {
    if (!user || !user.id) return;
    if (!filteredUsers || filteredUsers.length === 0) return;

    // Collect user IDs that we haven't fetched yet
    const toFetch = filteredUsers
      .map((u) => u.id)
      .filter((id) => !privacyProfiles[id]);

    // Limit number per batch to avoid flooding the server
    const batch = toFetch.slice(0, 15);
    if (batch.length === 0) return;

    batch.forEach((id) => {
      axios
        .get(
          `${config.apiUrl}/api/auth/profile/${id}?requestingUserId=${user.id}`
        )
        .then((res) => {
          setPrivacyProfiles((prev) => ({ ...prev, [id]: res.data }));
        })
        .catch((err) => {
          // Store a negative result to avoid retry loop; null profileImage will show placeholder
          setPrivacyProfiles((prev) => ({
            ...prev,
            [id]: { profileImage: null },
          }));
          console.error("Failed to fetch privacy profile for user", id, err);
        });
    });
  }, [filteredUsers, user, privacyProfiles]);

  // Handle window resize for responsive behavior
  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setShowChat(false); // Reset chat view on desktop
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Mark messages as delivered and read when chat is active
  React.useEffect(() => {
    if (to && safeMessages.length > 0) {
      // Mark messages as delivered when user is in the chat
      const sentMessages = safeMessages.filter(
        (msg) => msg.from === to && msg.to === user.id && msg.status === "sent"
      );

      // Mark messages as read when user is actively viewing the chat
      const unreadMessages = safeMessages.filter(
        (msg) =>
          msg.from === to &&
          msg.to === user.id &&
          (msg.status === "sent" || msg.status === "delivered")
      );

      if (sentMessages.length > 0) {
        // //console.log(
        //   "Marking messages as delivered:",
        //   sentMessages.length,
        //   "messages"
        // );
        markMessagesAsDelivered(user.id, to);
      }

      if (unreadMessages.length > 0) {
        // //console.log(
        //   "Marking messages as read:",
        //   unreadMessages.length,
        //   "messages"
        // );
        // Mark as read immediately when user opens the chat
        markMessagesAsRead(user.id, to);
      }
    }
  }, [to, safeMessages, user.id, markMessagesAsDelivered, markMessagesAsRead]);

  // Handle real-time message deletion
  React.useEffect(() => {
    const handleMessageDeleted = (data) => {
      //console.log("Home: Received messageDeleted event:", data);
      const { messageId, deleteType, updated } = data;

      if (deleteType === "forEveryone") {
        setPinnedMessages((prev) =>
          prev.filter((item) => String(item.messageId) !== String(messageId))
        );
        // Replace the message content with a system placeholder instead of removing
        setMessages((prev) => {
          if (!Array.isArray(prev)) return [];
          return prev.map((msg) =>
            msg._id === messageId
              ? {
                ...msg,
                text: updated?.text || "Sender deleted this message",
                messageType: "deleted",
              }
              : msg
          );
        });
      } else if (deleteType === "forMe") {
        // For "delete for me", remove the message from the current user's view
        setMessages((prev) => {
          if (!Array.isArray(prev)) return [];
          return prev.filter((msg) => msg._id !== messageId);
        });
      }
    };

    socket.on("messageDeleted", handleMessageDeleted);

    return () => {
      socket.off("messageDeleted", handleMessageDeleted);
    };
  }, [socket, messages]);

  // Real-time reaction updates
  React.useEffect(() => {
    const handleReactionUpdated = (payload) => {
      setMessages((prev) => {
        if (!Array.isArray(prev)) return [];
        return prev.map((m) =>
          m._id === payload.messageId
            ? { ...m, reactions: payload.reactions }
            : m
        );
      });
    };
    socket.on("messageReactionUpdated", handleReactionUpdated);
    return () => socket.off("messageReactionUpdated", handleReactionUpdated);
  }, [socket]);

  // Group update notifications
  React.useEffect(() => {
    const handleGroupUpdateNotification = (notificationData) => {
      const {
        type,
        groupName,
        updatedBy,
        changes,
        updatedGroup,
        groupId,
      } = notificationData || {};

      if (!updatedGroup || !groupId) return;

      const changeList = Array.isArray(changes) ? changes : [];
      let changeText = "";

      if (type === "adminTransferred") {
        changeText = "Group admin was updated";
      } else if (type === "memberLeft") {
        changeText = "Group membership was updated";
      } else if (changeList.length > 0) {
        if (
          changeList.includes("name") &&
          changeList.includes("description") &&
          changeList.includes("image")
        ) {
          changeText = "updated the group name, description, and image";
        } else if (changeList.includes("name") && changeList.includes("image")) {
          changeText = "updated the group name and image";
        } else if (changeList.includes("name") && changeList.includes("description")) {
          changeText = "updated the group name and description";
        } else if (changeList.includes("description") && changeList.includes("image")) {
          changeText = "updated the group description and image";
        } else if (changeList.includes("name")) {
          changeText = "changed the group name";
        } else if (changeList.includes("description")) {
          changeText = "updated the group description";
        } else if (changeList.includes("image")) {
          changeText = "changed the group image";
        }
      }

      if (
        changeText &&
        window.Notification &&
        Notification.permission === "granted"
      ) {
        const body = updatedBy ? `${updatedBy} ${changeText}` : changeText;
        new Notification(groupName || "Group", {
          body,
          icon: updatedGroup.avatar
            ? `${config.apiUrl}${updatedGroup.avatar}`
            : "/default-avatar.png",
          tag: `group-update-${groupId}`,
        });
      }

      if (selectedGroup && selectedGroup._id === groupId) {
        setSelectedGroup(updatedGroup);
      }

      if (groupUpdateCallback) {
        groupUpdateCallback(updatedGroup);
      }
    };

    socket.on("groupUpdateNotification", handleGroupUpdateNotification);
    return () =>
      socket.off("groupUpdateNotification", handleGroupUpdateNotification);
  }, [socket, selectedGroup, groupUpdateCallback]);

  // Listen for new group messages to update notification counts
  React.useEffect(() => {
    if (!socket || !user?.id) return;

    // Track processed message IDs to prevent duplicate increments
    const processedMessages = new Set();

    // Handle group message notifications (sent to individual user rooms)
    // This ensures notifications update immediately even if user is not viewing that group
    const handleGroupMessageNotification = (data) => {
      const { groupId, message } = data;
      const messageId = message._id?.toString() || message._id;
      
      // Skip if message already processed
      if (messageId && processedMessages.has(messageId)) {
        return;
      }
      
      // Only update count if:
      // 1. Message is not from the current user
      // 2. User is not currently viewing this group (if they are, messages are auto-marked as read)
      // 3. This is not a system message (system messages don't need notification counts)
      if (
        message.from && message.from._id !== user.id &&
        (!selectedGroup || selectedGroup._id !== groupId) &&
        message.messageType !== "system"
      ) {
        // Mark message as processed
        if (messageId) {
          processedMessages.add(messageId);
          // Clean up old message IDs after 1 minute to prevent memory leak
          setTimeout(() => processedMessages.delete(messageId), 60000);
        }
        
        // Update group notification counts immediately
        setGroupNotificationCounts((prev) => ({
          ...prev,
          [groupId]: (prev[groupId] || 0) + 1,
        }));

        // Update total group notification count immediately
        setGroupNotificationCount((prev) => prev + 1);
      }
    };

    // Remove receiveGroupMessage from notification handling
    // It should only be used for displaying messages in the active chat, not for notifications
    // This prevents duplicate increments

    // Listen to notification event (primary method - works for all groups)
    socket.on("groupMessageNotification", handleGroupMessageNotification);

    return () => {
      socket.off("groupMessageNotification", handleGroupMessageNotification);
      processedMessages.clear();
    };
  }, [socket, user?.id, selectedGroup]);

  // Additional trigger when chat becomes active
  React.useEffect(() => {
    if (to && safeMessages.length > 0) {
      // Force status update when chat is active
      const timer = setTimeout(() => {
        const sentMessages = safeMessages.filter(
          (msg) =>
            msg.from === to && msg.to === user.id && msg.status === "sent"
        );
        const unreadMessages = safeMessages.filter(
          (msg) =>
            msg.from === to &&
            msg.to === user.id &&
            (msg.status === "sent" || msg.status === "delivered")
        );

        if (sentMessages.length > 0) {
          // //console.log(
          //   "Active chat: Marking messages as delivered:",
          //   sentMessages.length,
          //   "messages"
          // );
          markMessagesAsDelivered(user.id, to);
        }

        if (unreadMessages.length > 0) {
          // //console.log(
          //   "Active chat: Marking messages as read:",
          //   unreadMessages.length,
          //   "messages"
          // );
          markMessagesAsRead(user.id, to);
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [to]); // Only trigger when 'to' changes

  // Fetch notification count for pending contact requests
  React.useEffect(() => {
    if (user && user.id) {
      fetchNotificationCount();
      fetchGroupNotificationCount();
      fetchVibeUsers();

      // Set up interval to refresh notification counts every 60 seconds (reduced frequency for better performance)
      const interval = setInterval(() => {
        fetchNotificationCount();
        fetchGroupNotificationCount();
        fetchVibeUsers();
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [user]);

  React.useEffect(() => {
    if (activeTab !== "chats") {
      setShowSidebarSearch(false);
      setSearchTerm("");
      return;
    }

    if (chatSubTab !== "recent") {
      setShowSidebarSearch(false);
      setSearchTerm("");
      return;
    }

    if (showSidebarSearch) {
      const timer = setTimeout(() => {
        document.querySelector('.search-input')?.focus();
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [activeTab, chatSubTab, showSidebarSearch]);

  // Get user's current location on mount for distance calculations
  React.useEffect(() => {
    const getUserLocation = async () => {
      try {
        const location = await getCurrentLocation();
        setCurrentUserLocation(location);
        //console.log("User location obtained:", location);
      } catch (error) {
        //console.log("Location access denied or unavailable:", error.message);
        // Don't show error to user, as location is optional for distance calculation
      }
    };

    getUserLocation();
  }, []);

  // Handle contact selection
  const handleContactClick = (userId) => {
    // Navigate to chats tab with chat query param
    navigate(`/chats?chat=${userId}`);
  };

  // Handle back button in mobile chat view
  const handleBackToContacts = () => {
    // Navigate to current tab without query params
    navigate(`/${activeTab}`);
  };

  // Show confirmation modal
  const showConfirmModal = (
    title,
    message,
    onConfirm,
    confirmText = "Confirm",
    cancelText = "Cancel"
  ) => {
    setConfirmModal({
      isVisible: true,
      title,
      message,
      onConfirm,
      confirmText,
      cancelText,
    });
  };

  // Hide confirmation modal
  const hideConfirmModal = () => {
    setConfirmModal({
      isVisible: false,
      title: "",
      message: "",
      onConfirm: null,
      confirmText: "Confirm",
      cancelText: "Cancel",
    });
  };

  // Handle confirmation
  const handleConfirmation = () => {
    if (confirmModal.onConfirm) {
      confirmModal.onConfirm();
    }
    hideConfirmModal();
  };

  // Handle chat menu click
  const handleChatMenuClick = (event, contactId) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;

    let position;
    if (isMobile) {
      // On mobile, show below the 3 dots button on the right side
      position = { 
        x: window.innerWidth - 220, // Align to right side with some padding
        y: rect.bottom + 5 // Position just below the button
      };
    } else {
      // On desktop, show near the click position
      position = {
        x: Math.min(event.clientX, window.innerWidth - 250),
        y: Math.min(event.clientY, window.innerHeight - 300),
      };
    }

    setContactMenu({
      isVisible: true,
      position,
      contactId,
    });
  };

  // Handle group menu click
  const handleGroupMenuClick = (event, groupId) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;

    let position;
    if (isMobile) {
      // On mobile, show below the 3 dots button on the right side
      position = { 
        x: window.innerWidth - 220, // Align to right side with some padding
        y: rect.bottom + 5 // Position just below the button
      };
    } else {
      // On desktop, show near the click position
      position = {
        x: Math.min(event.clientX, window.innerWidth - 250),
        y: Math.min(event.clientY, window.innerHeight - 300),
      };
    }

    setGroupMenu({
      isVisible: true,
      position,
      groupId,
    });
  };

  // Close group menu
  const closeGroupMenu = () => {
    setGroupMenu({
      isVisible: false,
      position: { x: 0, y: 0 },
      groupId: null,
    });
  };

  // Clear group history function (UI only - keeps database intact)
  const clearGroupHistory = (groupId) => {
    if (!groupId) return;
    const newClearedGroups = {
      ...clearedGroups,
      [groupId]: Date.now(),
    };
    setClearedGroups(newClearedGroups);
    try {
      localStorage.setItem("clearedGroups", JSON.stringify(newClearedGroups));
    } catch (error) {
      console.error("Error saving cleared groups:", error);
    }
    //console.log("Group chat history cleared from UI display only");
  };

  // Handle group menu actions
  const handleGroupMenuAction = (action) => {
    const groupId = groupMenu.groupId;
    closeGroupMenu();

    switch (action) {
      case "wallpaper":
        setShowWallpaperPicker(true);
        break;
      case "search":
        setShowChatSearch(true);
        break;
      case "viewGroup":
        setShowGroupMembersPage(true);
        break;
      case "clearHistory":
        if (groupId) {
          showConfirmModal(
            "Clear Group History",
            "Are you sure you want to clear chat history for this group?",
            () => clearGroupHistory(groupId),
            "Clear History",
            "Cancel"
          );
        }
        break;
      case "leaveGroup":
        if (groupId) {
          openLeaveGroupChoice(groupId);
        }
        break;
      default:
        break;
    }
  };

  const notifyGroupListStorageUpdated = () => {
    window.dispatchEvent(new CustomEvent("groupListStorageUpdated"));
  };

  const getGroupMetaForLeave = (groupId) => {
    const g =
      selectedGroup?._id === groupId
        ? selectedGroup
        : readOnlyGroups[groupId] || hiddenGroups[groupId];
    return {
      name: g?.name || "Group",
      avatar: g?.avatar ?? null,
    };
  };

  const openLeaveGroupChoice = (groupId) => {
    setLeaveGroupChoiceModal({ isVisible: true, groupId });
  };

  const closeLeaveGroupModals = () => {
    setLeaveGroupChoiceModal({ isVisible: false, groupId: null });
    setAdminLeaveModal({ isVisible: false, groupId: null });
    setPendingLeaveOptions({ deleteFromList: false });
  };

  const proceedAfterLeaveChoice = (deleteFromList) => {
    const groupId = leaveGroupChoiceModal.groupId;
    if (!groupId) return;

    setPendingLeaveOptions({ deleteFromList });
    setLeaveGroupChoiceModal({ isVisible: false, groupId: null });

    if (selectedGroup?._id === groupId && isGroupAdmin(selectedGroup, user.id)) {
      setAdminLeaveModal({ isVisible: true, groupId });
      return;
    }

    leaveGroup(groupId, null, { deleteFromList });
  };

  const getLeaveModalGroup = () => {
    const groupId = adminLeaveModal.groupId || leaveGroupChoiceModal.groupId;
    if (selectedGroup && selectedGroup._id === groupId) return selectedGroup;
    return null;
  };

  const renderLeaveGroupModals = () => {
    const modalGroup = getLeaveModalGroup();
    const choiceGroupId = leaveGroupChoiceModal.groupId;
    const choiceGroupName =
      modalGroup?.name ||
      (choiceGroupId ? getGroupMetaForLeave(choiceGroupId).name : null);

    return (
      <>
        {leaveGroupChoiceModal.isVisible && (
          <LeaveGroupChoiceModal
            groupName={choiceGroupName}
            loading={leaveActionLoading}
            onCancel={closeLeaveGroupModals}
            onLeaveOnly={() => proceedAfterLeaveChoice(false)}
            onDeleteForSelf={() => proceedAfterLeaveChoice(true)}
          />
        )}
        {adminLeaveModal.isVisible && modalGroup && (
          <AdminLeaveGroupModal
            group={modalGroup}
            user={user}
            loading={adminLeaveLoading || leaveActionLoading}
            onCancel={() => {
              setAdminLeaveModal({ isVisible: false, groupId: null });
              setPendingLeaveOptions({ deleteFromList: false });
            }}
            onConfirm={handleAdminLeaveConfirm}
          />
        )}
      </>
    );
  };

  const leaveGroup = async (groupId, newAdminId = null, options = {}) => {
    const { deleteFromList = false } = options;
    const meta = getGroupMetaForLeave(groupId);

    try {
      setLeaveActionLoading(true);
      setAdminLeaveLoading(true);
      await axios.delete(`${config.apiUrl}/api/groups/${groupId}/members/${user.id}`, {
        data: { userId: user.id, ...(newAdminId ? { newAdminId } : {}) },
      });

      if (deleteFromList) {
        const nextHidden = hideGroupFromList(groupId, meta);
        setHiddenGroups(nextHidden);
        const nextReadOnly = removeFromReadOnlyGroups(groupId);
        setReadOnlyGroups(nextReadOnly);
        clearGroupHistory(groupId);
        removeGroupFromListCallback?.(groupId);
        if (selectedGroup && selectedGroup._id === groupId) {
          setSelectedGroup(null);
          setShowGroupMembersPage(false);
          setShowChat(false);
        }
      } else {
        const nextReadOnly = addToReadOnlyGroups(groupId, meta);
        setReadOnlyGroups(nextReadOnly);
        const nextHidden = unhideGroup(groupId);
        setHiddenGroups(nextHidden);
        if (selectedGroup && selectedGroup._id === groupId) {
          setSelectedGroup((g) => (g ? { ...g, members: [] } : g));
          setShowGroupMembersPage(false);
        }
      }

      setGroupNotificationCounts((prev) => {
        const cleared = prev[groupId] || 0;
        setGroupNotificationCount((count) => Math.max(0, count - cleared));
        const next = { ...prev };
        delete next[groupId];
        return next;
      });

      notifyGroupListStorageUpdated();
    } catch (error) {
      console.error("Error leaving group:", error);
      const errCode = error.response?.data?.code;
      const errMsg = error.response?.data?.error || "Failed to leave group";
      if (errCode === "ADMIN_TRANSFER_REQUIRED") {
        setAdminLeaveModal({ isVisible: true, groupId });
      } else {
        alert(errMsg);
      }
    } finally {
      setLeaveActionLoading(false);
      setAdminLeaveLoading(false);
      setAdminLeaveModal({ isVisible: false, groupId: null });
      setPendingLeaveOptions({ deleteFromList: false });
    }
  };

  const handleAdminLeaveConfirm = (newAdminId) => {
    const groupId = adminLeaveModal.groupId || selectedGroup?._id;
    if (groupId) {
      leaveGroup(groupId, newAdminId, { deleteFromList: pendingLeaveOptions.deleteFromList });
    }
  };

  // Close contact menu
  const closeContactMenu = () => {
    setContactMenu({
      isVisible: false,
      position: { x: 0, y: 0 },
      contactId: null,
    });
  };

  const lockChat = (contactId) => {
    const next = { ...lockedChats, [contactId]: { lockedAt: Date.now() } };
    setLockedChats(next);
    saveLockedChats(next);
    setUnlockedChats((prev) => {
      const copy = { ...prev };
      delete copy[contactId];
      return copy;
    });
  };

  const unlockChatPermanently = (contactId) => {
    const next = { ...lockedChats };
    delete next[contactId];
    setLockedChats(next);
    saveLockedChats(next);
    setUnlockedChats((prev) => {
      const copy = { ...prev };
      delete copy[contactId];
      return copy;
    });
  };

  const unlockChatSession = (contactId) => {
    setUnlockedChats((prev) => ({ ...prev, [contactId]: true }));
    setChatLockError("");
  };

  const handleLockChatRequest = (contactId) => {
    if (!contactId) return;
    if (!hasChatLockPin(user.id)) {
      setChatLockError("");
      setChatLockModal({
        isVisible: true,
        mode: "setup",
        pendingContactId: contactId,
      });
      return;
    }
    lockChat(contactId);
  };

  const closeChatLockModal = () => {
    setChatLockModal({ isVisible: false, mode: "setup", pendingContactId: null });
    setChatLockError("");
  };

  const handleChatLockModalSubmit = (pin) => {
    if (chatLockModal.mode === "setup") {
      saveChatLockPin(user.id, pin);
      const contactId = chatLockModal.pendingContactId;
      closeChatLockModal();
      if (contactId) lockChat(contactId);
    }
  };

  const handleChatLockUnlock = (pin) => {
    if (!verifyChatLockPin(user.id, pin)) {
      setChatLockError("Incorrect PIN. Try again.");
      setChatLockPinFailCount((n) => n + 1);
      return;
    }
    setChatLockError("");
    if (to) unlockChatSession(to);
  };

  const handleChatLockPinResetSuccess = (newPin) => {
    saveChatLockPin(user.id, newPin);
    setShowChatLockPinReset(false);
    setChatLockError("");
    if (to) unlockChatSession(to);
  };

  const isChatUnlocked = (contactId) => Boolean(unlockedChats[contactId]);
  const isCurrentChatLockedAndSealed =
    Boolean(to && isChatLocked(lockedChats, to) && !isChatUnlocked(to));

  // Re-lock when leaving a chat (session unlock only)
  useEffect(() => {
    setUnlockedChats({});
    setChatLockError("");
    setChatLockPinFailCount(0);
  }, [to]);

  // Handle contact menu actions
  const handleContactMenuAction = (action) => {
    const contactId = contactMenu.contactId;
    closeContactMenu();

    switch (action) {
      case "wallpaper":
        // Open wallpaper picker for current chat
        setShowWallpaperPicker(true);
        break;
      case "search":
        // Open chat search
        setShowChatSearch(true);
        break;
      case "clearHistory":
        // Clear chat history for this contact
        if (contactId) {
          showConfirmModal(
            "Clear Chat History",
            "Are you sure you want to clear chat history with this contact?",
            () => clearChatHistory(contactId),
            "Clear History",
            "Cancel"
          );
        }
        break;
      case "viewContact":
        // Open user profile
        if (contactId) {
          handleOpenUserProfile(contactId);
        }
        break;
      case "sharedContent":
        if (contactId) {
          handleOpenSharedContent(contactId);
        }
        break;
      case "lockChat":
        if (contactId) {
          handleLockChatRequest(contactId);
        }
        break;
      case "unlockChat":
        if (contactId) {
          unlockChatPermanently(contactId);
        }
        break;
      case "block":
        // Block user
        if (contactId) {
          showConfirmModal(
            "Block Contact",
            "Are you sure you want to block this contact? You will not be able to send or receive messages from them.",
            () => blockUser(contactId),
            "Block",
            "Cancel"
          );
        }
        break;
      case "unblock":
        // Unblock user
        if (contactId) {
          showConfirmModal(
            "Unblock Contact",
            "Are you sure you want to unblock this contact? They will be able to send you messages again.",
            () => unblockUser(contactId),
            "Unblock",
            "Cancel"
          );
        }
        break;
      default:
        break;
    }
  };

  // Clear chat history function (UI only - keeps database intact)
  const clearChatHistory = (contactId) => {
    // Only clear the local messages state for display
    if (to === contactId) {
      setMessages([]);

      // Mark this chat as cleared in localStorage
      const newClearedChats = {
        ...clearedChats,
        [contactId]: Date.now(), // Store timestamp of when it was cleared
      };
      setClearedChats(newClearedChats);

      try {
        localStorage.setItem("clearedChats", JSON.stringify(newClearedChats));
      } catch (error) {
        console.error("Error saving cleared chats:", error);
      }

      //console.log("Chat history cleared from UI display only");
    } else {
      alert("Please open the chat to clear its history from view.");
    }
  };

  // Block user function
  const blockUser = async (contactId) => {
    try {
      const response = await axios.post(
        `${config.apiUrl}/api/auth/block-user`,
        {
          userId: user.id,
          targetUserId: contactId,
        }
      );
      //console.log("User blocked:", response.data);

      // Mark this user as blocked in localStorage and state
      const newBlockedUsers = {
        ...blockedUsers,
        [contactId]: {
          blockedAt: Date.now(),
          username:
            users.find((u) => u.id === contactId)?.username || "Unknown User",
        },
      };
      setBlockedUsers(newBlockedUsers);

      try {
        localStorage.setItem("blockedUsers", JSON.stringify(newBlockedUsers));
      } catch (error) {
        console.error("Error saving blocked users:", error);
      }

      // Note: Block status is already shown in the chat header, no need for additional message...

      // Emit socket event for real-time updates
      socket.emit("userBlocked", {
        blockerId: user.id,
        blockedUserId: contactId,
      });

      // Refresh users list
      refreshUsers(true);

      // Clear chat menu
      closeContactMenu();
    } catch (error) {
      console.error("Error blocking user:", error);
      alert("Failed to block contact. Please try again.");
    }
  };

  // Unblock user function
  const unblockUser = async (contactId) => {
    try {
      const response = await axios.post(
        `${config.apiUrl}/api/auth/unblock-user`,
        {
          userId: user.id,
          targetUserId: contactId,
        }
      );
      //console.log("User unblocked:", response.data);

      // Remove this user from blocked users list
      const newBlockedUsers = { ...blockedUsers };
      delete newBlockedUsers[contactId];
      setBlockedUsers(newBlockedUsers);

      try {
        localStorage.setItem("blockedUsers", JSON.stringify(newBlockedUsers));
      } catch (error) {
        console.error("Error saving unblocked users:", error);
      }

      // Note: Block status is already shown in the chat header, no need for additional message

      // Emit socket event for real-time updates
      socket.emit("userUnblocked", {
        unblockerId: user.id,
        unblockedUserId: contactId,
      });

      // Refresh users list
      refreshUsers(true);

      // Clear chat menu
      closeContactMenu();
    } catch (error) {
      console.error("Error unblocking user:", error);
      alert("Failed to unblock contact. Please try again.");
    }
  };

  // Log call to history
  const logCall = (userId, userName, callType, direction, status = "completed") => {
    const newCall = {
      userId,
      userName,
      type: callType,
      direction,
      status, // 'completed', 'missed'
      timestamp: new Date().toISOString(),
    };

    setCallHistory((prev) => {
      const updated = [newCall, ...prev].slice(0, 50);
      try {
        localStorage.setItem("callHistory", JSON.stringify(updated));
      } catch (e) {
        console.error("Error saving call history:", e);
      }
      return updated;
    });
  };

  // Handle call end
  const handleCallEnd = (callData) => {
    if (callData && callData.userId && callData.userName) {
      logCall(
        callData.userId,
        callData.userName,
        callData.type || "audio",
        callData.direction || "outgoing",
        callData.status || "completed"
      );
    }
    setIsCallActive(false);
  };

  // The 1:1 AudioCall / VideoCall components are mounted globally in App.js
  // (to prevent duplicate socket listeners). They forward end-of-call data
  // through this `callLogEntry` window event so we can keep populating the
  // call history list here in Home.js.
  useEffect(() => {
    const onCallLog = (e) => {
      if (e?.detail) handleCallEnd(e.detail);
    };
    window.addEventListener('callLogEntry', onCallLog);
    return () => window.removeEventListener('callLogEntry', onCallLog);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle image click to open in fullscreen
  const handleImageClick = (imageUrl) => {
    openImageViewer(imageUrl, setImageViewer, config.apiUrl);
  };

  // Handle closing the image viewer
  const handleCloseImageViewer = () => {
    closeImageViewer(setImageViewer);
  };

  // Handle document download
  const handleDocumentDownload = async (documentUrl, documentName) => {
    try {
      const response = await fetch(
        getDocumentDownloadUrl(documentUrl, config.apiUrl)
      );
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element and click it to trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = documentName;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading document:", error);
      alert("Failed to download document. Please try again.");
    }
  };

  // Context menu handlers
  const handleMessageContextMenu = (event, message) => {
    event.preventDefault();
    event.stopPropagation();

    // //console.log("Context menu triggered for message:", {
    //   messageId: message._id,
    //   message: message,
    //   hasId: !!message._id,
    // });

    const rect = event.currentTarget.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;

    let position;
    if (isMobile) {
      // On mobile, show at bottom
      position = { x: 0, y: window.innerHeight - 300 };
    } else {
      // On desktop, show near the click position
      position = {
        x: Math.min(event.clientX, window.innerWidth - 250),
        y: Math.min(event.clientY, window.innerHeight - 300),
      };
    }

    setContextMenu({
      isVisible: true,
      position,
      message,
    });
  };

  const closeContextMenu = () => {
    setContextMenu({
      isVisible: false,
      position: { x: 0, y: 0 },
      message: null,
    });
  };

  const handleDeleteMessage = async (deleteType) => {
    try {
      // Check if setMessages is available
      if (typeof setMessages !== "function") {
        console.error("setMessages is not defined or not a function");
        alert("Error: setMessages function is not available");
        return;
      }

      if (!contextMenu.message || !contextMenu.message._id) {
        console.error("No message selected for deletion");
        alert("No message selected for deletion");
        return;
      }

      // //console.log("Attempting to delete message:", {
      //   messageId: contextMenu.message._id,
      //   messageIdString: contextMenu.message._id.toString(),
      //   deleteType,
      //   userId: user.id,
      //   apiUrl: `${config.apiUrl}/api/messages/${contextMenu.message._id}`,
      //   message: contextMenu.message,
      //   setMessagesAvailable: typeof setMessages === "function",
      // });

      const response = await axios.delete(
        `${config.apiUrl}/api/messages/${contextMenu.message._id}`,
        {
          data: {
            deleteType,
            userId: user.id,
          },
        }
      );

      //console.log("Delete response:", response.data);

      // Emit socket event for real-time deletion notification
      socket.emit("messageDeleted", {
        messageId: contextMenu.message._id.toString(),
        deleteType,
        userId: user.id,
      });

      if (deleteType === "forEveryone") {
        setPinnedMessages((prev) =>
          prev.filter((item) => String(item.messageId) !== String(contextMenu.message._id))
        );
      }

      if (deleteType === "forMe") {
        // For "Delete for me", only remove from local state
        setMessages((prev) => {
          if (!Array.isArray(prev)) return [];
          return prev.filter((msg) => msg._id !== contextMenu.message._id);
        });
      } else {
        // For "Delete for everyone", refresh messages to get updated list
        // This will also update the other user's view
        if (to) {
          try {
            const updatedMessages = await axios.get(
              `${config.apiUrl}/api/messages/${user.id}/${to}`
            );
            // Handle new API response format with block status
            if (
              updatedMessages.data.messages &&
              updatedMessages.data.blockStatus
            ) {
              setMessages(updatedMessages.data.messages);
            } else {
              // Fallback for old API format
              setMessages(updatedMessages.data);
            }
          } catch (err) {
            console.error(
              "Failed to refresh messages after delete for everyone:",
              err
            );
            // Fallback: remove from local state
            setMessages((prev) => {
              if (!Array.isArray(prev)) return [];
              return prev.filter((msg) => msg._id !== contextMenu.message._id);
            });
          }
        }
      }

      // Update user list
      if (user) {
        axios
          .get(`${config.apiUrl}/api/auth/users-with-messages/${user.id}`)
          .then((res) => {
            setUsers(res.data);
          })
          .catch((err) => {
            console.error("Failed to refresh users with messages:", err);
          });
      }

      closeContextMenu();
    } catch (error) {
      console.error("Error deleting message:", error);
      // console.error("Error details:", {
      //   message: error.message,
      //   response: error.response?.data,
      //   status: error.response?.status,
      //   messageId: contextMenu.message?._id,
      //   setMessagesAvailable: typeof setMessages === "function",
      // });
      alert(
        `Failed to delete message. Error: ${error.response?.data?.error || error.message
        }`
      );
    }
  };

  const handlePinMessage = async (messageId, isPinned) => {
    try {
      let response;
      if (selectedGroup) {
        response = await axios.put(
          `${config.apiUrl}/api/groups/messages/${messageId}/pin`,
          {
            isPinned,
            userId: user.id,
            groupId: selectedGroup._id,
          }
        );
      } else {
        response = await axios.put(
          `${config.apiUrl}/api/messages/${messageId}/pin`,
          {
            isPinned,
            userId: user.id,
          }
        );
      }

      if (Array.isArray(response.data?.pinnedMessages)) {
        setPinnedMessages(response.data.pinnedMessages);
      }
    } catch (error) {
      console.error("Error pinning message:", error);
      alert(
        `Failed to ${isPinned ? "pin" : "unpin"} message. ${
          error.response?.data?.error || error.message
        }`
      );
    }
  };

  // Star/unstar a direct message (per-user)
  const handleStarMessage = async (messageId, isStarred) => {
    try {
      const response = await axios.put(
        `${config.apiUrl}/api/messages/${messageId}/star`,
        {
          isStarred,
          userId: user.id,
        }
      );

      setMessages((prev) => {
        if (!Array.isArray(prev)) return [];
        return prev.map((msg) => {
          if (msg._id !== messageId) return msg;
          let starredBy = Array.isArray(msg.starredBy) ? [...msg.starredBy] : [];
          if (isStarred) {
            if (!starredBy.includes(user.id)) starredBy.push(user.id);
          } else {
            starredBy = starredBy.filter((id) => id !== user.id);
          }
          return { ...msg, starredBy };
        });
      });
    } catch (error) {
      console.error("Error starring message:", error);
      alert(
        `Failed to star message. Error: ${error.response?.data?.error || error.message}`
      );
    }
  };

  const handleReplyToMessage = (message) => {
    setReplyTo(message);
    closeContextMenu();
  };

  // Open the forward picker for the selected message.
  const handleForwardMessage = (message) => {
    setForwardModal({ isVisible: true, message });
    closeContextMenu();
  };

  const closeForwardModal = () => {
    setForwardModal({ isVisible: false, message: null });
  };

  // Forward the selected message to the chosen users/groups.
  const handleForwardConfirm = async (targets) => {
    const message = forwardModal.message;
    if (!message || !message._id || !Array.isArray(targets) || targets.length === 0) {
      return;
    }
    // Home's own message list / context menu is used only for 1:1 (direct)
    // chats. Group messages are handled inside the GroupMessages component.
    try {
      await axios.post(`${config.apiUrl}/api/messages/forward`, {
        from: user.id,
        messageIds: [message._id],
        sourceType: "direct",
        targets: targets.map((t) => ({ type: t.type, id: t.id })),
      });
    } catch (error) {
      console.error("Error forwarding message:", error);
      alert("Failed to forward message. Please try again.");
    } finally {
      closeForwardModal();
    }
  };

  // Edit a message
  const handleEditMessage = (message) => {
    setEditMessage(message);
    setText(message.text);
    closeContextMenu();
  };

  // Save edited message
  const handleSaveEdit = async () => {
    if (!editMessage || !text.trim()) return;

    try {
      if (selectedGroup) {
        // Group message edit
        const response = await axios.put(
          `${config.apiUrl}/api/groups/messages/${editMessage._id}`,
          {
            text: text.trim(),
            userId: user.id,
            groupId: selectedGroup._id,
          }
        );

        // Socket event will handle the update for all group members
        // No need to manually update local state here as socket will handle it

        // Clear edit state
        setEditMessage(null);
        setText("");
      } else if (to) {
        // Regular message edit
        const response = await axios.put(
          `${config.apiUrl}/api/messages/${editMessage._id}`,
          {
            text: text.trim(),
            userId: user.id,
          }
        );

        // Update message in local state
        setMessages((prev) => {
          if (!Array.isArray(prev)) return [];
          return prev.map((msg) =>
            msg._id === editMessage._id
              ? { ...msg, text: text.trim(), isEdited: true }
              : msg
          );
        });

        // Emit socket event for real-time update
        socket.emit("messageEdited", {
          messageId: editMessage._id,
          text: text.trim(),
          chatId: to,
        });

        // Clear edit state
        setEditMessage(null);
        setText("");
      }
    } catch (error) {
      console.error("Error editing message:", error);
      alert(
        `Failed to edit message. Error: ${
          error.response?.data?.error || error.message
        }`
      );
    }
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditMessage(null);
    setText("");
  };

  // Global search handler
  const handleGlobalSearch = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setGlobalSearchResults([]);
      return;
    }

    try {
      const response = await axios.get(
        `${config.apiUrl}/api/messages/search/${user.id}?q=${encodeURIComponent(searchQuery.trim())}`
      );
      setGlobalSearchResults(response.data.results || []);
    } catch (error) {
      console.error("Error searching messages:", error);
      setGlobalSearchResults([]);
    }
  };

  // Debounced search effect
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (globalSearchTerm) {
        handleGlobalSearch(globalSearchTerm);
      } else {
        setGlobalSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [globalSearchTerm]);

  // Highlight search term in text
  const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="search-highlight">{part}</mark>
      ) : (
        part
      )
    );
  };

  // Update search matches when search term changes
  React.useEffect(() => {
    if (chatSearchTerm && displayMessages.length > 0) {
      const matches = displayMessages
        .map((msg, index) => ({ msg, index }))
        .filter(({ msg }) => 
          msg.text && msg.text.toLowerCase().includes(chatSearchTerm.toLowerCase())
        );
      setSearchMatches(matches);
      setSearchMatchIndex(0);
      
      // Scroll to first match
      if (matches.length > 0) {
        setTimeout(() => {
          scrollToMessage(matches[0].msg._id);
        }, 100);
      }
    } else {
      setSearchMatches([]);
      setSearchMatchIndex(0);
    }
  }, [chatSearchTerm, displayMessages.length]);

  // Scroll to specific message
  const scrollToMessage = (messageId) => {
    const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      messageElement.classList.add('search-highlight-active');
      
      // Remove highlight after animation
      setTimeout(() => {
        messageElement.classList.remove('search-highlight-active');
      }, 2000);
    }
  };

  // Navigate to next search match
  const goToNextMatch = () => {
    if (searchMatches.length === 0) return;
    
    const nextIndex = (searchMatchIndex + 1) % searchMatches.length;
    setSearchMatchIndex(nextIndex);
    scrollToMessage(searchMatches[nextIndex].msg._id);
  };

  // Navigate to previous search match
  const goToPreviousMatch = () => {
    if (searchMatches.length === 0) return;
    
    const prevIndex = searchMatchIndex === 0 ? searchMatches.length - 1 : searchMatchIndex - 1;
    setSearchMatchIndex(prevIndex);
    scrollToMessage(searchMatches[prevIndex].msg._id);
  };

  // React to a message (pass falsy type to remove own reaction)
  const handleReactToMessage = async (message, type) => {
    try {
      const currentUserId = user?.id?.toString?.() || "";
      // Optimistic UI update
      setMessages((prev) => {
        if (!Array.isArray(prev)) return [];
        return prev.map((m) => {
          if (m._id !== message._id) return m;
          const reactions = (m.reactions || []).filter((r) => {
            const reactionUserId = (
              r?.userId?._id ||
              r?.userId?.id ||
              r?.userId
            )?.toString?.();
            return reactionUserId !== currentUserId;
          });
          if (type)
            reactions.push({
              userId: user.id,
              type,
              reactedAt: new Date().toISOString(),
            });
          return { ...m, reactions };
        });
      });

      // Persist to server
      await axios.put(`${config.apiUrl}/api/messages/${message._id}/reaction`, {
        userId: user.id,
        type: type || null,
      });

      // Emit socket for realtime sync
      socket.emit("messageReaction", {
        messageId: message._id,
        userId: user.id,
        type: type || null,
      });
    } catch (error) {
      console.error("Error reacting to message:", error);
    }
  };

  const openReactionDetails = (message) => {
    if (!message?.reactions?.length) return;
    setReactionDetailsMessageId(message._id);
  };

  const closeReactionDetails = () => {
    setReactionDetailsMessageId(null);
  };

  const handleRemoveOwnReaction = (message) => {
    handleReactToMessage(message, null);
    closeReactionDetails();
  };

  const getReactionUserId = (reaction) => {
    return (
      reaction?.userId?._id ||
      reaction?.userId?.id ||
      reaction?.userId
    )?.toString?.() || "";
  };

  const getDisplayNameFromUserObj = (userObj) => {
    if (!userObj || typeof userObj !== "object") return "";
    return (
      userObj.username ||
      userObj.name ||
      userObj.phoneNumber ||
      userObj.phone ||
      ""
    );
  };

  const getReactionUserDisplayName = (reaction) => {
    const reactionUserId = getReactionUserId(reaction);
    if (!reactionUserId) return "Unknown user";
    if (reactionUserId === user?.id?.toString?.()) return "You";

    const directName = getDisplayNameFromUserObj(reaction?.userId);
    if (directName) return directName;

    const matchedUser = Array.isArray(users)
      ? users.find((u) => (u?.id || u?._id)?.toString?.() === reactionUserId)
      : null;
    if (matchedUser?.username) return matchedUser.username;

    if (to && reactionUserId === to?.toString?.()) {
      return (
        selectedUserProfileData?.username ||
        matchedUser?.name ||
        "Contact"
      );
    }

    return "Unknown user";
  };

  React.useEffect(() => {
    // Close reaction details when switching away from one-to-one chat
    if (!to || selectedGroup) {
      setReactionDetailsMessageId(null);
    }
  }, [to, selectedGroup]);

  // Fetch notification count for pending contact requests
  const fetchNotificationCount = async () => {
    try {
      const response = await axios.get(
        `${config.apiUrl}/api/contacts/${user.id}/pending-count`
      );
      setNotificationCount(response.data.count);
    } catch (error) {
      console.error("Error fetching notification count:", error);
    }
  };

  // Fetch group notification count
  const fetchGroupNotificationCount = async () => {
    try {
      const response = await axios.get(
        `${config.apiUrl}/api/groups/user/${user.id}/notification-count`
      );
      setGroupNotificationCount(response.data.totalCount);
      setGroupNotificationCounts(response.data.groupCounts);
    } catch (error) {
      console.error("Error fetching group notification count:", error);
    }
  };

  // Fetch contacts who have active vibes/statuses (own statuses always first)
  const fetchVibeUsers = async () => {
    try {
      const [allRes, myRes] = await Promise.all([
        axios.get(`${config.apiUrl}/api/status/all/${user.id}`),
        axios.get(`${config.apiUrl}/api/status/my/${user.id}`),
      ]);
      const contactStatuses = allRes.data;
      const ownStatuses = myRes.data;

      // Group contacts by user
      const userMap = {};
      contactStatuses.forEach((s) => {
        const uid = s.userId?._id || s.userId;
        if (!userMap[uid]) {
          userMap[uid] = {
            userId: uid,
            username: s.userId?.username || s.username || "User",
            profileImage: s.userId?.profileImage || null,
            latestStatus: s,
            statuses: [],
            isOwn: false,
          };
        }
        userMap[uid].statuses.push(s);
      });

      // Build "My Vibe" entry (own statuses) – always first
      const myVibeEntry = {
        userId: user.id,
        username: user.username || "My Vibe",
        profileImage: user.profileImage || null,
        latestStatus: ownStatuses[0] || null,
        statuses: ownStatuses,
        isOwn: true,
      };

      // Remove current user from contacts list if present
      delete userMap[user.id];

      setVibeUsers([myVibeEntry, ...Object.values(userMap)]);
    } catch (error) {
      console.error("Error fetching vibe users:", error);
    }
  };

  const formatVibeTimeAgo = (timestamp) => {
    const now = new Date();
    const vibeTime = new Date(timestamp);
    const diffMs = now - vibeTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return vibeTime.toLocaleDateString();
  };

  const getNestedUserId = (value) => {
    if (!value) return null;
    if (typeof value === "string" || typeof value === "number") {
      return value.toString();
    }

    return (
      value.userId?._id?.toString?.() ||
      value.userId?.toString?.() ||
      value._id?.toString?.() ||
      value.id?.toString?.() ||
      null
    );
  };

  const getVibeOwnerId = (status, vibeUser) => {
    const fromVibeUser = vibeUser?.userId?.toString?.() || vibeUser?.userId;
    if (fromVibeUser) return fromVibeUser;
    return (
      status?.userId?._id?.toString?.() ||
      status?.userId?.toString?.() ||
      null
    );
  };

  /** Views from others only — exclude the vibe author */
  const getViewsExcludingOwner = (views, ownerId) => {
    if (!ownerId) return views || [];
    return (views || []).filter((view) => getNestedUserId(view) !== ownerId);
  };

  const resetVibeViewerPanels = () => {
    setActiveVibePanel(null);
    setCurrentStatusViewers([]);
    setComments([]);
    setNewComment("");
    setLoadingComments(false);
    setShowEmojiPicker(false);
    setStatusLikes([]);
    setCommentLikes([]);
  };

  const updateVibeStatusState = (statusId, updater) => {
    setSelectedVibeStatuses((prev) =>
      prev.map((status) => (status._id === statusId ? updater(status) : status))
    );

    setVibeUsers((prev) =>
      prev
        .map((vibeUser) => {
          if (!(vibeUser.statuses || []).some((status) => status._id === statusId)) {
            return vibeUser;
          }

          const statuses = (vibeUser.statuses || []).map((status) =>
            status._id === statusId ? updater(status) : status
          );

          return {
            ...vibeUser,
            statuses,
            latestStatus:
              vibeUser.latestStatus?._id === statusId
                ? statuses.find((status) => status._id === statusId) || null
                : vibeUser.latestStatus,
          };
        })
        .filter((vibeUser) => vibeUser.isOwn || (vibeUser.statuses || []).length > 0)
    );
  };

  const removeVibeStatusFromState = (statusId) => {
    setSelectedVibeStatuses((prev) => {
      const next = prev.filter((status) => status._id !== statusId);
      setSelectedVibeIndex((currentIndex) => {
        if (next.length === 0) return 0;
        return Math.min(currentIndex, next.length - 1);
      });
      return next;
    });

    setVibeUsers((prev) =>
      prev
        .map((vibeUser) => {
          const statuses = (vibeUser.statuses || []).filter(
            (status) => status._id !== statusId
          );

          return {
            ...vibeUser,
            statuses,
            latestStatus:
              vibeUser.latestStatus?._id === statusId
                ? statuses[0] || null
                : vibeUser.latestStatus,
          };
        })
        .filter((vibeUser) => vibeUser.isOwn || (vibeUser.statuses || []).length > 0)
    );
  };

  const closeVibeViewer = () => {
    setShowVibeViewer(false);
    setSelectedVibeStatuses([]);
    setSelectedVibeIndex(0);
    setSelectedVibeUser(null);
    resetVibeViewerPanels();
  };

  const markVibesAsViewed = async (statusesToMark, vibeUser = null) => {
    const currentUserId = user.id?.toString?.();
    if (!currentUserId) return;

    const statusIdsToMark = new Set(
      (statusesToMark || []).map((status) => status._id)
    );

    // Optimistic UI update so viewed ring changes immediately without refresh.
    const applyLocalView = (status) => {
      if (!statusIdsToMark.has(status._id)) return status;

      const ownerId = getVibeOwnerId(status, vibeUser);
      if (ownerId === currentUserId) return status;

      const alreadyViewed = (status.views || []).some(
        (view) => getNestedUserId(view) === currentUserId
      );
      if (alreadyViewed) return status;

      return {
        ...status,
        views: [
          ...(status.views || []),
          { userId: currentUserId, viewedAt: new Date().toISOString() },
        ],
      };
    };

    setSelectedVibeStatuses((prev) => prev.map(applyLocalView));
    setVibeUsers((prev) =>
      prev.map((vibeUser) => ({
        ...vibeUser,
        statuses: (vibeUser.statuses || []).map(applyLocalView),
      }))
    );

    try {
      for (const status of statusesToMark) {
        const ownerId = getVibeOwnerId(status, vibeUser);
        if (ownerId === currentUserId) continue;

        const alreadyViewed = status.views?.some((view) => view.userId === user.id || view.userId?._id === user.id);
        if (alreadyViewed) continue;

        await axios.post(`${config.apiUrl}/api/status/${status._id}/view`, {
          userId: user.id,
        });

        if (socket && status.userId && (status.userId._id || status.userId) !== user.id) {
          socket.emit("statusViewed", {
            statusId: status._id,
            viewerId: user.id,
            statusOwnerId: status.userId._id || status.userId,
          });
        }
      }
    } catch (error) {
      console.error("Failed to mark vibes as viewed:", error);
    }
  };

  const openVibeViewerAtIndex = (vibeUser, startIndex = 0) => {
    const orderedStatuses = [...(vibeUser.statuses || [])].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );
    if (orderedStatuses.length === 0) return;

    const safeStartIndex = Math.max(
      0,
      Math.min(startIndex, orderedStatuses.length - 1)
    );

    resetVibeViewerPanels();
    setSelectedVibeUser(vibeUser);
    setSelectedVibeStatuses(orderedStatuses);
    setSelectedVibeIndex(safeStartIndex);
    setShowVibeViewer(true);
    markVibesAsViewed(orderedStatuses, vibeUser);
  };

  const openVibeViewer = (vibeUser) => {
    openVibeViewerAtIndex(vibeUser, 0);
  };

  const showStatusViewers = async (status) => {
    if (!status?._id) return;

    const ownerId = getVibeOwnerId(status, selectedVibeUser);
    const viewsForList = getViewsExcludingOwner(status.views, ownerId);

    try {
      const viewersWithDetails = await Promise.all(
        viewsForList.map(async (view) => {
          const viewerId = getNestedUserId(view);
          try {
            const response = await axios.get(
              `${config.apiUrl}/api/auth/profile/${viewerId}`
            );
            return {
              ...response.data,
              viewedAt: view.viewedAt,
            };
          } catch (error) {
            console.error("Error fetching viewer details:", error);
            return {
              id: viewerId,
              username: "Unknown User",
              profileImage: null,
              viewedAt: view.viewedAt,
            };
          }
        })
      );

      viewersWithDetails.sort(
        (a, b) => new Date(b.viewedAt) - new Date(a.viewedAt)
      );
      setCurrentStatusViewers(viewersWithDetails);
      setActiveVibePanel("viewers");
    } catch (error) {
      console.error("Error fetching status viewers:", error);
    }
  };

  const fetchComments = async (statusId) => {
    try {
      setLoadingComments(true);
      const response = await axios.get(
        `${config.apiUrl}/api/status/${statusId}/comments`
      );
      setComments(response.data.comments || []);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    } finally {
      setLoadingComments(false);
    }
  };

  const openComments = async (status) => {
    if (!status?._id) return;
    setActiveVibePanel("comments");
    await fetchComments(status._id);
  };

  const addComment = async () => {
    if (!newComment.trim() || !currentVibeStatus?._id) return;

    try {
      const response = await axios.post(
        `${config.apiUrl}/api/status/${currentVibeStatus._id}/comment`,
        {
          userId: user.id,
          text: newComment.trim(),
        }
      );

      const createdComment = response.data.comment;
      setComments((prev) => [...prev, createdComment]);
      updateVibeStatusState(currentVibeStatus._id, (status) => ({
        ...status,
        comments: [...(status.comments || []), createdComment],
      }));
      setNewComment("");
      setShowEmojiPicker(false);

      const statusOwnerId = getNestedUserId(currentVibeStatus.userId);
      if (socket && statusOwnerId && statusOwnerId !== user.id?.toString?.()) {
        socket.emit("statusCommented", {
          statusId: currentVibeStatus._id,
          commenterId: user.id,
          statusOwnerId,
          comment: createdComment,
        });
      }
    } catch (error) {
      console.error("Failed to add comment:", error);
      alert("Failed to add comment. Please try again.");
    }
  };

  const toggleCommentLike = async (commentId) => {
    if (!currentVibeStatus?._id) return;

    const existingComment = comments.find((comment) => comment._id === commentId);
    const isLiked = (existingComment?.likes || []).some(
      (like) => getNestedUserId(like) === user.id?.toString?.()
    );

    setComments((prev) =>
      prev.map((comment) => {
        if (comment._id !== commentId) return comment;
        const likes = isLiked
          ? (comment.likes || []).filter(
              (like) => getNestedUserId(like) !== user.id?.toString?.()
            )
          : [...(comment.likes || []), { userId: user.id, likedAt: new Date() }];
        return {
          ...comment,
          likes,
          likesCount: likes.length,
        };
      })
    );

    try {
      const response = await axios.post(
        `${config.apiUrl}/api/status/${currentVibeStatus._id}/comment/${commentId}/like`,
        {
          userId: user.id,
        }
      );

      if (socket) {
        socket.emit("commentLiked", {
          statusId: currentVibeStatus._id,
          commentId,
          likerId: user.id,
          statusOwnerId: getNestedUserId(currentVibeStatus.userId),
          action: response.data.action,
          likesCount: response.data.likesCount,
        });
      }
    } catch (error) {
      console.error("Failed to toggle comment like:", error);
      fetchComments(currentVibeStatus._id);
    }
  };

  const toggleStatusLike = async () => {
    if (!currentVibeStatus?._id) return;

    const isLiked = (currentVibeStatus.likes || []).some(
      (like) => getNestedUserId(like) === user.id?.toString?.()
    );

    updateVibeStatusState(currentVibeStatus._id, (status) => ({
      ...status,
      likes: isLiked
        ? (status.likes || []).filter(
            (like) => getNestedUserId(like) !== user.id?.toString?.()
          )
        : [...(status.likes || []), { userId: user.id, likedAt: new Date() }],
    }));

    try {
      const response = await axios.post(
        `${config.apiUrl}/api/status/${currentVibeStatus._id}/like`,
        {
          userId: user.id,
        }
      );

      if (socket) {
        socket.emit("statusLiked", {
          statusId: currentVibeStatus._id,
          likerId: user.id,
          statusOwnerId: getNestedUserId(currentVibeStatus.userId),
          action: response.data.action,
          likesCount: response.data.likesCount,
        });
      }
    } catch (error) {
      console.error("Failed to toggle status like:", error);
      updateVibeStatusState(currentVibeStatus._id, (status) => ({
        ...status,
        likes: isLiked
          ? [...(status.likes || []), { userId: user.id, likedAt: new Date() }]
          : (status.likes || []).filter(
              (like) => getNestedUserId(like) !== user.id?.toString?.()
            ),
      }));
    }
  };

  const fetchStatusLikes = async (statusId) => {
    try {
      const response = await axios.get(
        `${config.apiUrl}/api/status/${statusId}/likes`
      );
      setStatusLikes(response.data.likes || []);
      setActiveVibePanel("likes");
    } catch (error) {
      console.error("Failed to fetch status likes:", error);
    }
  };

  const fetchCommentLikes = async (commentId) => {
    if (!currentVibeStatus?._id) return;

    try {
      const response = await axios.get(
        `${config.apiUrl}/api/status/${currentVibeStatus._id}/comment/${commentId}/likes`
      );
      setCommentLikes(response.data.likes || []);
      setActiveVibePanel("commentLikes");
    } catch (error) {
      console.error("Failed to fetch comment likes:", error);
    }
  };

  const handleEmojiSelect = (emoji) => {
    setNewComment((prev) => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handleDeleteCurrentVibe = () => {
    if (!currentVibeStatus?._id) return;

    showConfirmModal(
      "Delete Vibe",
      "Are you sure you want to delete this vibe?",
      async () => {
        try {
          await axios.delete(
            `${config.apiUrl}/api/status/${currentVibeStatus._id}?userId=${user.id}`
          );

          if (socket) {
            socket.emit("statusDeleted", {
              userId: user.id,
              statusId: currentVibeStatus._id,
            });
          }

          const remainingStatuses = selectedVibeStatuses.filter(
            (status) => status._id !== currentVibeStatus._id
          );
          removeVibeStatusFromState(currentVibeStatus._id);

          if (remainingStatuses.length === 0) {
            closeVibeViewer();
          } else {
            resetVibeViewerPanels();
          }

          fetchVibeUsers();
        } catch (error) {
          console.error("Failed to delete vibe:", error);
          alert("Failed to delete vibe. Please try again.");
        }
      },
      "Delete"
    );
  };

  const handleAddToMyVibe = () => {
    setShowVibeUploadModal(true);
  };

  const handleSidebarSearchToggle = () => {
    setShowSidebarSearch((prev) => {
      const next = !prev;
      if (!next) {
        setSearchTerm("");
      }
      return next;
    });
  };

  const formatVibeDuration = (durationHours) => {
    if (durationHours === 1) return "1 Hour";
    if (durationHours === 24) return "1 Day";
    if (durationHours === 72) return "3 Days";
    if (durationHours === 168) return "1 Week";
    return `${durationHours || 24} Hours`;
  };

  const getVibeExpiresAt = (status) => {
    if (!status) return null;
    if (status.expiresAt) {
      return new Date(status.expiresAt);
    }
    if (status.createdAt && status.duration) {
      return new Date(
        new Date(status.createdAt).getTime() + status.duration * 60 * 60 * 1000
      );
    }
    if (status.createdAt) {
      return new Date(new Date(status.createdAt).getTime() + 24 * 60 * 60 * 1000);
    }
    return null;
  };

  const getVibeTimeRemaining = (expiresAt) => {
    if (!expiresAt) return "Unknown";

    try {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diffMs = expiry - now;

      if (diffMs <= 0) return "Expired";

      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 60) return `${diffMins}m remaining`;
      if (diffHours < 24) return `${diffHours}h remaining`;
      return `${Math.floor(diffDays)}d remaining`;
    } catch (error) {
      console.error("Error calculating vibe time remaining:", error);
      return "Unknown";
    }
  };

  const currentVibeStatus = selectedVibeStatuses[selectedVibeIndex];
  const isOwnSelectedVibe = selectedVibeUser?.isOwn === true;
  const myVibeUser = vibeUsers.find((vu) => vu.isOwn);
  const contactVibeUsers = vibeUsers.filter((vu) => !vu.isOwn);
  const navigableVibeUsers = vibeUsers.filter(
    (vu) => (vu.statuses || []).length > 0
  );
  const selectedVibeUserId = selectedVibeUser?.userId?.toString?.();
  const selectedNavigableVibeIndex = navigableVibeUsers.findIndex(
    (vu) => vu.userId?.toString?.() === selectedVibeUserId
  );
  const previousNavigableVibeUser =
    selectedNavigableVibeIndex > 0
      ? navigableVibeUsers[selectedNavigableVibeIndex - 1]
      : null;
  const nextNavigableVibeUser =
    selectedNavigableVibeIndex >= 0 && selectedNavigableVibeIndex < navigableVibeUsers.length - 1
      ? navigableVibeUsers[selectedNavigableVibeIndex + 1]
      : null;
  const canOpenPreviousVibeUser =
    !!previousNavigableVibeUser && selectedVibeIndex === 0;
  const canOpenNextVibeUser =
    !!nextNavigableVibeUser && selectedVibeIndex === selectedVibeStatuses.length - 1;

  // Handle opening user profile
  const handleOpenUserProfile = (userId) => {
    setSelectedUserForProfile(userId);
    setShowUserProfile(true);
  };

  // Handle closing user profile
  const handleCloseUserProfile = () => {
    setShowUserProfile(false);
    setSelectedUserForProfile(null);
  };

  const handleOpenSharedContent = (contactId) => {
    if (!contactId) return;
    setSharedContentContactId(contactId);
    setShowSharedContentModal(true);
  };

  const handleCloseSharedContent = () => {
    setShowSharedContentModal(false);
    setSharedContentContactId(null);
  };

  // Handle user block/unblock
  const handleUserBlock = (userId) => {
    // Refresh users list to update any blocked status
    // Add a small delay to ensure backend has processed the block action
    setTimeout(() => {
      refreshUsers(true); // Force refresh to bypass cache
    }, 500);
  };

  const handleUserUnblock = (userId) => {
    // Refresh users list to update any unblocked status
    // Add a small delay to ensure backend has processed the unblock action
    setTimeout(() => {
      refreshUsers(true); // Force refresh to bypass cache
    }, 500);
  };

  // When opening a 1:1 chat, force scroll to latest message once messages load
  React.useEffect(() => {
    if (to) {
      stickToBottomOnOpenRef.current = true;
    }
  }, [to]);

  // When opening a group chat, retry scroll after GroupMessages renders
  React.useEffect(() => {
    if (!selectedGroup) return;
    const delays = [50, 150, 350, 600];
    const timers = delays.map((ms) => setTimeout(scrollToBottom, ms));
    return () => timers.forEach(clearTimeout);
  }, [selectedGroup?._id]);

  // Close search bar when switching to a different chat
  React.useEffect(() => {
    if (to) {
      setShowChatSearch(false);
      setChatSearchTerm("");
      setSearchMatches([]);
      setSearchMatchIndex(0);
    }
  }, [to]);

  // Sync block status when chat is opened
  React.useEffect(() => {
    if (to && blockStatus) {
      // Update blockedUsers state based on blockStatus from backend
      const currentBlockedUsers = { ...blockedUsers };

      // If user is blocked by me (isToBlockedByFrom), add to blockedUsers
      if (blockStatus.isToBlockedByFrom && !currentBlockedUsers[to]) {
        currentBlockedUsers[to] = {
          blockedAt: Date.now(),
          username: users.find((u) => u.id === to)?.username || "Unknown User",
        };
        setBlockedUsers(currentBlockedUsers);

        // Save to localStorage
        try {
          localStorage.setItem(
            "blockedUsers",
            JSON.stringify(currentBlockedUsers)
          );
        } catch (error) {
          console.error("Error saving blocked users:", error);
        }
      }

      // If user is not blocked by me (!isToBlockedByFrom), remove from blockedUsers if it exists
      if (!blockStatus.isToBlockedByFrom && currentBlockedUsers[to]) {
        delete currentBlockedUsers[to];
        setBlockedUsers(currentBlockedUsers);

        // Save to localStorage
        try {
          localStorage.setItem(
            "blockedUsers",
            JSON.stringify(currentBlockedUsers)
          );
        } catch (error) {
          console.error("Error saving unblocked users:", error);
        }
      }
    }
  }, [to, blockStatus, users]);

  // Fetch privacy-filtered profile for the currently selected user so that
  // profile photo (and other future fields) respect privacy settings.
  React.useEffect(() => {
    let isCancelled = false;
    const fetchSelectedUserProfile = async () => {
      if (!to || !user) {
        setSelectedUserProfileData(null);
        return;
      }
      // Clear immediately so avatar hides right away while we refetch
      setSelectedUserProfileData(null);
      try {
        const resp = await axios.get(
          `${config.apiUrl}/api/auth/profile/${to}?requestingUserId=${user.id}`
        );
        if (!isCancelled) {
          setSelectedUserProfileData(resp.data);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error(
            "Failed to fetch selected user profile (privacy-aware):",
            err
          );
          setSelectedUserProfileData(null);
        }
      }
    };
    fetchSelectedUserProfile();
    return () => {
      isCancelled = true;
    };
  }, [to, user, blockStatus]);

  // Auto-scroll to bottom when opening a chat or when new messages arrive
  React.useEffect(() => {
    if (!to || safeMessages.length === 0) return;

    // After opening a chat, always jump to the latest message (with retries for layout)
    if (stickToBottomOnOpenRef.current) {
      const delays = [0, 50, 150, 350];
      const timers = delays.map((ms, idx) =>
        setTimeout(() => {
          scrollToBottom();
          if (idx === delays.length - 1) {
            stickToBottomOnOpenRef.current = false;
          }
        }, ms)
      );
      return () => timers.forEach(clearTimeout);
    }

    // Otherwise only auto-scroll if user is already near the bottom
    if (chatWindowRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatWindowRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;

      if (isNearBottom) {
        const timer = setTimeout(scrollToBottom, 50);
        return () => clearTimeout(timer);
      }
    }
  }, [safeMessages.length, to]);

  useEffect(() => {
    const onJoinFinished = () => setJoiningOngoingCallId(null);
    window.addEventListener('groupCallJoinFinished', onJoinFinished);
    return () => window.removeEventListener('groupCallJoinFinished', onJoinFinished);
  }, []);

  // If showing group members page, render that instead
  if (showGroupMembersPage && selectedGroup) {
    return (
      <>
      <GroupMembersPage
        group={selectedGroup}
        user={user}
        onBack={() => setShowGroupMembersPage(false)}
        onRequestLeaveGroup={() => openLeaveGroupChoice(selectedGroup._id)}
        onGroupUpdated={(updatedGroup) => {
          setSelectedGroup(updatedGroup);
          // Update the groups list if callback is available
          if (groupUpdateCallback) {
            groupUpdateCallback(updatedGroup);
          }
          // If user re-joined, clear read-only flag
          setReadOnlyGroups((prev) => {
            const next = { ...prev };
            const isMember = Array.isArray(updatedGroup.members) && updatedGroup.members.some((m) => (m.user?._id || m.user || m).toString?.() === user.id || ((m.user?._id || m.user) === user.id));
            if (isMember && next[updatedGroup._id]) {
              delete next[updatedGroup._id];
              saveReadOnlyGroups(next);
            }
            if (isMember) {
              const nextHidden = unhideGroup(updatedGroup._id);
              setHiddenGroups(nextHidden);
            }
            return next;
          });
        }}
      />
      {renderLeaveGroupModals()}
      </>
    );
  }

  return (
    <div className={`app-container ${isWidePanelTab ? "wide-panel-layout" : ""}`}>
      {/* Side Navigation */}
      <SideNavbar
        user={user}
        onProfileClick={() => navigate("/profile")}
        notificationCount={notificationCount}
        groupNotificationCount={groupNotificationCount}
        hasActiveGroupCall={joinableGroupCalls.length > 0}
        isMobile={isMobile}
        showChat={showChat}
      />

      {/* Contacts/Sidebar - Always visible on desktop, conditionally on mobile */}
      <div className={`sidebar ${isMobile && showChat ? "hidden" : ""} ${isWidePanelTab ? "sidebar-wide-content" : ""} ${activeTab === "chats" ? "sidebar-chats-layout" : ""}`}>
        {activeTab === "chats" && (
          <>
            <div className="sidebar-header">
              <div className="app-header app-header--chats">
                <div className="app-header-toolbar">
                  <div className="app-brand-block">
                    <h1
                      className="app-brand-name notranslate"
                      title="Universal Chat & Messaging Advisory App"
                      onClick={() => navigate("/chats")}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          navigate("/chats");
                        }
                      }}
                    >
                      UCMAAPP
                    </h1>
                  <span className="app-brand-version notranslate">Alpha Version</span>
                  </div>
                  <div className="header-right-actions">
                    <button className="header-icon-btn" onClick={handleSidebarSearchToggle} aria-label="Search">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                    </button>
                    <button
                      type="button"
                      className="header-profile-btn"
                      onClick={() => navigate("/profile")}
                      aria-label="Your profile"
                    >
                      <span className="header-profile-avatar">
                        {user?.profileImage ? (
                          <img src={`${config.apiUrl}${user.profileImage}`} alt="" className="header-profile-img" />
                        ) : (
                          <span className="header-profile-placeholder notranslate">
                            {(user?.username || "U").charAt(0).toUpperCase()}
                          </span>
                        )}
                      </span>
                      {isPremium && <PremiumBadge size="sm" />}
                    </button>
                  </div>
                </div>
                {/* Sub-tabs: Recent Chats / Requests */}
                <div className="chat-sub-tabs">
                  <button className={`chat-sub-tab${chatSubTab === "recent" ? " active" : ""}`} onClick={() => setChatSubTab("recent")}>
                    Recent Chats
                  </button>
                  <button className={`chat-sub-tab${chatSubTab === "requests" ? " active" : ""}`} onClick={() => setChatSubTab("requests")}>
                    Requests
                    {notificationCount > 0 && <span className="sub-tab-badge">{notificationCount}</span>}
                  </button>
                </div>
              </div>
            </div>

            <div className="chats-sheet-wrap">
            <div className="chats-panel">
            {/* Vibe / Status bubbles — horizontal scroll below header */}
            {chatSubTab === "recent" && (
              <div className="vibe-bubbles-section">
                <div className="vibe-bubbles-label">Active Vibes</div>
                <div className="vibe-bubbles-scroll">
                  <div className="vibe-bubble vibe-bubble-add" onClick={handleAddToMyVibe} title="Add Vibe">
                    <div className="vibe-avatar-ring vibe-avatar-ring-add">
                      {myVibeUser?.profileImage ? (
                        <img src={`${config.apiUrl}${myVibeUser.profileImage}`} alt="Add Vibe" className="vibe-avatar-img" />
                      ) : (
                        <div className="vibe-avatar-placeholder">{(user.username || "A").charAt(0).toUpperCase()}</div>
                      )}
                      <span className="vibe-my-plus-badge">+</span>
                    </div>
                    <span className="vibe-username">Add Vibe</span>
                  </div>

                  {myVibeUser?.statuses?.length > 0 && (
                    <div
                      className="vibe-bubble vibe-bubble-my"
                      onClick={() => openVibeViewer(myVibeUser)}
                      title="My Vibe"
                    >
                      <div className="vibe-avatar-ring vibe-avatar-ring-add">
                        {myVibeUser?.profileImage ? (
                          <img src={`${config.apiUrl}${myVibeUser.profileImage}`} alt="My Vibe" className="vibe-avatar-img" />
                        ) : (
                          <div className="vibe-avatar-placeholder">{(user.username || "M").charAt(0).toUpperCase()}</div>
                        )}
                      </div>
                      <span className="vibe-username notranslate">My Vibe</span>
                    </div>
                  )}

                  {contactVibeUsers.map((vu) => {
                    // Determine if ALL statuses for this contact have been viewed by the current user
                    const currentUserId = user.id?.toString?.();
                    const allViewed = vu.statuses.length > 0 && vu.statuses.every((s) =>
                      (s.views || []).some(
                        (v) => (v.userId?._id || v.userId)?.toString?.() === currentUserId
                      )
                    );

                    return (
                      <div
                        key={vu.userId}
                        className="vibe-bubble"
                        onClick={() => openVibeViewer(vu)}
                        title={vu.username}
                      >
                        <div className={`vibe-avatar-ring${allViewed ? " vibe-avatar-ring-viewed" : ""}`}>
                          {vu.profileImage ? (
                            <img src={`${config.apiUrl}${vu.profileImage}`} alt={vu.username} className="vibe-avatar-img" />
                          ) : (
                            <div className="vibe-avatar-placeholder">{vu.username.charAt(0).toUpperCase()}</div>
                          )}
                        </div>
                        <span className="vibe-username notranslate">{vu.username.length > 8 ? vu.username.slice(0, 8) + "..." : vu.username}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {chatSubTab === "recent" && (
              <>
                {showSidebarSearch && (
                  <div className="search-container">
                    <div className="search-input-wrapper">
                      <span className="search-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                      </span>
                      <input
                        type="text"
                        placeholder="Search or start new chat"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                      />
                    </div>
                  </div>
                )}
                <div className="user-list">
              {/* Permanent UCMA AI assistant — always pinned at the very top,
                  always online. Opens in the main chat pane (sidebar stays). */}
              {(!searchTerm ||
                "ucma ai".includes(searchTerm.toLowerCase()) ||
                searchTerm.toLowerCase().includes("ai")) && (
                <div
                  className={`user-item ucma-ai-item${aiChatOpen ? " active" : ""}`}
                  onClick={() => {
                    setAiChatOpen(true);
                    if (selectedGroup && socket) {
                      socket.emit("leaveGroup", selectedGroup._id);
                    }
                    setSelectedGroup(null);
                    setTo("");
                    if (isMobile) {
                      setShowChat(true);
                    }
                    // Clear chat/group URL params so selection syncs with AI pane
                    if (chatUserId || groupId) {
                      navigate("/chats", { replace: true });
                    }
                  }}
                >
                  <div className="user-avatar">
                    <img
                      src="/ucma_ai_avatar.png"
                      alt="UCMA AI"
                      className="notranslate"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "50%",
                        display: "block",
                      }}
                    />
                  </div>
                  <div className="user-info">
                    <div className="user-details">
                      <div className="user-name notranslate">
                        UCMA AI
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: 10,
                            fontWeight: 600,
                            color: "#7c3aed",
                            background: "rgba(124,58,237,0.12)",
                            padding: "1px 6px",
                            borderRadius: 8,
                          }}
                        >
                          AI
                        </span>
                      </div>
                    </div>
                    <div className="user-last-message">
                      <div className="last-message">
                        <span style={{ color: "#25d366" }}>● </span>
                        Always online · Ask me anything
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {filteredUsers.map((u) => (
                <div
                  key={u.id}
                  className={`user-item${to === u.id ? " active" : ""}`}
                  onClick={() => handleContactClick(u.id)}
                >
                  <div className="user-avatar">
                    {(() => {
                      // Use privacy-filtered profile image if fetched; if not yet fetched or null, show placeholder
                      const privacyImage = privacyProfiles[u.id]?.profileImage;
                      if (privacyImage) {
                        const loadingStatus = profileImageLoading[u.id];
                        const isErrored = loadingStatus === 'error';
                        const isLoading = loadingStatus !== false && !isErrored;
                        return (
                          <div className="avatar-image-wrapper">
                            {isLoading && (
                              <div className="avatar-loading-spinner" aria-label="Loading avatar" />
                            )}
                            {!isErrored ? (
                              <img
                                src={`${config.apiUrl}${privacyImage}`}
                                alt={u.username}
                                className={`user-profile-image ${isLoading ? 'loading' : 'loaded'}`}
                                onLoad={() => setProfileImageLoading(prev => ({ ...prev, [u.id]: false }))}
                                onError={() => setProfileImageLoading(prev => ({ ...prev, [u.id]: 'error' }))}
                              />
                            ) : (
                              <div className="user-avatar-placeholder notranslate">
                                {u.username.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                        );
                      }
                      return (
                        <div className="user-avatar-placeholder notranslate">
                          {u.username.charAt(0).toUpperCase()}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="user-info">
                    <div className="user-details">
                      <div
                        className="user-name clickable-username notranslate"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenUserProfile(u.id);
                        }}
                        title="View profile"
                      >
                        {u.username}
                        {isChatLocked(lockedChats, u.id) && (
                          <span className="chat-lock-icon-inline" title="Locked chat">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                              <path d="M7 11V7a5 5 0 0110 0v4" />
                            </svg>
                          </span>
                        )}
                      </div>
                      <div className="message-time">
                        {u.lastMessage
                          ? formatLastMessageTime(u.lastMessage.createdAt)
                          : ""}
                      </div>
                    </div>
                    <div className="user-last-message">
                      <div className="last-message">
                        {isChatLocked(lockedChats, u.id) ? (
                          <span className="locked-chat-preview">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                              <path d="M7 11V7a5 5 0 0110 0v4" />
                            </svg>
                            Locked chat
                          </span>
                        ) : u.lastMessage ? (
                          <>
                            {u.lastMessage.from === user.id && (
                              <span className="message-prefix">You: </span>
                            )}
                            <span className="notranslate">
                              {u.lastMessage.messageType === "voice" ? (
                                <span className="voice-indicator">
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                    <line x1="12" y1="19" x2="12" y2="23" />
                                    <line x1="8" y1="23" x2="16" y2="23" />
                                  </svg>
                                  Voice message
                                </span>
                              ) : u.lastMessage.messageType === "contact" ||
                                u.lastMessage.type === "contact" ? (
                                <span className="contact-indicator">
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                  <circle cx="12" cy="7" r="4" />
                                </svg>
                                  Contact
                                </span>
                              ) : u.lastMessage.messageType === "call" ||
                                u.lastMessage.type === "call" ? (
                                <span className="call-indicator">
                                  {u.lastMessage.callData?.callType === "video" ? (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M23 7l-7 5 7 5V7z" />
                                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                    </svg>
                                  ) : (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.36 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.34 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                                    </svg>
                                  )}
                                  {u.lastMessage.callData?.callType === "video" ? "Video call" : "Voice call"}
                                </span>
                              ) : (
                                u.lastMessage.text
                              )}
                            </span>
                          </>
                        ) : (
                          "Click to start chatting"
                        )}
                      </div>
                      <div className="message-indicators">
                        {u.unreadCount > 0 && (
                          <span className="unread-count">{u.unreadCount}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
              </>
            )}

            {/* Requests sub-tab — reuse Contacts component in pending mode */}
            {chatSubTab === "requests" && (
              <Contacts
                user={user}
                onContactClick={handleContactClick}
                onProfileClick={handleOpenUserProfile}
                refreshUsers={refreshUsers}
                refreshNotificationCount={fetchNotificationCount}
                socket={socket}
                initialTab="pending"
              />
            )}

            </div>
            </div>

            {showVibeViewer && currentVibeStatus && (
              <div className="inline-vibe-viewer-modal" onClick={closeVibeViewer}>
                <div className="inline-vibe-viewer-content" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-vibe-viewer-header">
                    <div className="inline-vibe-viewer-user">
                      <div className="inline-vibe-viewer-avatar">
                        {selectedVibeUser?.profileImage ? (
                          <img src={`${config.apiUrl}${selectedVibeUser.profileImage}`} alt={selectedVibeUser.username} />
                        ) : (
                          <div className="inline-vibe-viewer-avatar-placeholder notranslate">
                            {selectedVibeUser?.username?.charAt(0)?.toUpperCase() || "U"}
                          </div>
                        )}
                      </div>
                      <div className="inline-vibe-viewer-meta">
                        <span className="inline-vibe-viewer-name notranslate">{selectedVibeUser?.username || "User"}</span>
                        <span className="inline-vibe-viewer-duration">
                          {formatVibeDuration(currentVibeStatus.duration)} vibe
                          {" • "}
                          {getVibeTimeRemaining(getVibeExpiresAt(currentVibeStatus))}
                        </span>
                        <span className="inline-vibe-viewer-time">{formatVibeTimeAgo(currentVibeStatus.createdAt)}</span>
                      </div>
                    </div>
                    <div className="inline-vibe-header-actions">
                      {isOwnSelectedVibe ? (
                        <>
                          <button
                            className={`inline-vibe-action-btn${activeVibePanel === "viewers" ? " active" : ""}`}
                            onClick={() => showStatusViewers(currentVibeStatus)}
                            aria-label="Show vibe viewers"
                            title="Viewers"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                            <span>
                              {getViewsExcludingOwner(
                                currentVibeStatus.views,
                                getVibeOwnerId(currentVibeStatus, selectedVibeUser)
                              ).length}
                            </span>
                          </button>
                          <button
                            className={`inline-vibe-action-btn${activeVibePanel === "likes" ? " active" : ""}`}
                            onClick={() => fetchStatusLikes(currentVibeStatus._id)}
                            aria-label="Show vibe likes"
                            title="Likes"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                            </svg>
                            <span>{currentVibeStatus.likes?.length || 0}</span>
                          </button>
                        </>
                      ) : (
                        <button
                          className={`inline-vibe-action-btn${(currentVibeStatus.likes || []).some((like) => getNestedUserId(like) === user.id?.toString?.()) ? " active liked" : ""}`}
                          onClick={toggleStatusLike}
                          aria-label="Like vibe"
                          title={(currentVibeStatus.likes || []).some((like) => getNestedUserId(like) === user.id?.toString?.()) ? "Unlike" : "Like"}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                          </svg>
                          <span
                            className={currentVibeStatus.likes?.length ? "clickable-count" : ""}
                            onClick={(event) => {
                              if (!currentVibeStatus.likes?.length) return;
                              event.stopPropagation();
                              fetchStatusLikes(currentVibeStatus._id);
                            }}
                          >
                            {currentVibeStatus.likes?.length || 0}
                          </span>
                        </button>
                      )}
                      <button
                        className={`inline-vibe-action-btn${activeVibePanel === "comments" ? " active" : ""}`}
                        onClick={() => openComments(currentVibeStatus)}
                        aria-label="Open vibe comments"
                        title="Comments"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        <span>{currentVibeStatus.comments?.length || 0}</span>
                      </button>
                      {isOwnSelectedVibe && (
                        <button
                          className="inline-vibe-action-btn danger"
                          onClick={handleDeleteCurrentVibe}
                          aria-label="Delete vibe"
                          title="Delete"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      )}
                      <button className="inline-vibe-close-btn" onClick={closeVibeViewer} aria-label="Close vibe viewer">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                      </button>
                    </div>
                  </div>

                  <div className="inline-vibe-progress">
                    {selectedVibeStatuses.map((status, index) => (
                      <button
                        key={status._id || index}
                        className={`inline-vibe-progress-bar${index === selectedVibeIndex ? " active" : ""}`}
                        onClick={() => setSelectedVibeIndex(index)}
                        aria-label={`Open vibe ${index + 1}`}
                      />
                    ))}
                  </div>

                  <div className="inline-vibe-viewer-body">
                    {currentVibeStatus.text && (
                      <div className="inline-vibe-text notranslate">
                        <p>{currentVibeStatus.text}</p>
                      </div>
                    )}

                    {currentVibeStatus.fileUrl && (
                      <div className="inline-vibe-media">
                        {currentVibeStatus.fileType?.startsWith("image/") ? (
                          <img src={`${config.apiUrl}${currentVibeStatus.fileUrl}`} alt="Vibe" className="inline-vibe-image" />
                        ) : currentVibeStatus.fileType?.startsWith("video/") ? (
                          <video src={`${config.apiUrl}${currentVibeStatus.fileUrl}`} controls className="inline-vibe-video" />
                        ) : null}
                      </div>
                    )}

                    {activeVibePanel === "viewers" && (
                      <div className="inline-vibe-panel">
                        <div className="inline-vibe-panel-header">
                          <h3>Viewed by {currentStatusViewers.length}</h3>
                        </div>
                        <div className="inline-vibe-panel-list">
                          {currentStatusViewers.length > 0 ? (
                            currentStatusViewers.map((viewer) => (
                              <div key={viewer.id || viewer.username} className="inline-vibe-list-item">
                                <div className="inline-vibe-list-avatar">
                                  {viewer.profileImage ? (
                                    <img src={`${config.apiUrl}${viewer.profileImage}`} alt={viewer.username} />
                                  ) : (
                                    <div className="inline-vibe-list-avatar-placeholder notranslate">
                                      {viewer.username?.charAt(0)?.toUpperCase() || "U"}
                                    </div>
                                  )}
                                </div>
                                <div className="inline-vibe-list-meta">
                                  <span className="inline-vibe-list-title notranslate">{viewer.username || "Unknown User"}</span>
                                  <span className="inline-vibe-list-subtitle">{formatVibeTimeAgo(viewer.viewedAt)}</span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="inline-vibe-empty-state">No one has viewed this vibe yet</div>
                          )}
                        </div>
                      </div>
                    )}

                    {activeVibePanel === "likes" && (
                      <div className="inline-vibe-panel">
                        <div className="inline-vibe-panel-header">
                          <h3>Liked by {statusLikes.length}</h3>
                        </div>
                        <div className="inline-vibe-panel-list">
                          {statusLikes.length > 0 ? (
                            statusLikes.map((like) => (
                              <div key={like._id || like.user?._id} className="inline-vibe-list-item">
                                <div className="inline-vibe-list-avatar">
                                  {like.user?.profileImage ? (
                                    <img src={`${config.apiUrl}${like.user.profileImage}`} alt={like.user.username} />
                                  ) : (
                                    <div className="inline-vibe-list-avatar-placeholder notranslate">
                                      {like.user?.username?.charAt(0)?.toUpperCase() || "U"}
                                    </div>
                                  )}
                                </div>
                                <div className="inline-vibe-list-meta">
                                  <span className="inline-vibe-list-title notranslate">{like.user?.username || "Unknown User"}</span>
                                  <span className="inline-vibe-list-subtitle">{formatVibeTimeAgo(like.likedAt)}</span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="inline-vibe-empty-state">No likes yet</div>
                          )}
                        </div>
                      </div>
                    )}

                    {activeVibePanel === "comments" && (
                      <div className="inline-vibe-panel comments">
                        <div className="inline-vibe-panel-header">
                          <h3>Comments</h3>
                        </div>
                        <div className="inline-vibe-panel-list comments-list">
                          {loadingComments ? (
                            <div className="inline-vibe-empty-state">Loading comments...</div>
                          ) : comments.length > 0 ? (
                            comments.map((comment) => (
                              <div key={comment._id} className="inline-vibe-comment-item">
                                <div className="inline-vibe-list-avatar">
                                  {comment.user?.profileImage ? (
                                    <img src={`${config.apiUrl}${comment.user.profileImage}`} alt={comment.user.username} />
                                  ) : (
                                    <div className="inline-vibe-list-avatar-placeholder notranslate">
                                      {comment.user?.username?.charAt(0)?.toUpperCase() || "U"}
                                    </div>
                                  )}
                                </div>
                                <div className="inline-vibe-comment-body">
                                  <div className="inline-vibe-comment-header">
                                    <span className="inline-vibe-list-title notranslate">{comment.user?.username || "Unknown User"}</span>
                                    <span className="inline-vibe-list-subtitle">{formatVibeTimeAgo(comment.createdAt)}</span>
                                  </div>
                                  <div className="inline-vibe-comment-text notranslate">{comment.text}</div>
                                  <button
                                    className={`inline-vibe-comment-like${(comment.likes || []).some((like) => getNestedUserId(like) === user.id?.toString?.()) ? " liked" : ""}`}
                                    onClick={() => toggleCommentLike(comment._id)}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                                    </svg>
                                    <span
                                      className={comment.likesCount > 0 ? "clickable-count" : ""}
                                      onClick={(event) => {
                                        if (!comment.likesCount) return;
                                        event.stopPropagation();
                                        fetchCommentLikes(comment._id);
                                      }}
                                    >
                                      {comment.likesCount || 0}
                                    </span>
                                  </button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="inline-vibe-empty-state">No comments yet</div>
                          )}
                        </div>
                        <div className="inline-vibe-comment-input-wrap">
                          <div className="inline-vibe-comment-input-row">
                            <input
                              type="text"
                              value={newComment}
                              onChange={(event) => setNewComment(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                  addComment();
                                }
                              }}
                              placeholder="Add a comment..."
                              maxLength={500}
                            />
                            <button
                              className="inline-vibe-comment-icon-btn"
                              onClick={() => setShowEmojiPicker((prev) => !prev)}
                              title="Add emoji"
                            >
                              😊
                            </button>
                            <button
                              className="inline-vibe-comment-send-btn"
                              onClick={addComment}
                              disabled={!newComment.trim()}
                              title="Send comment"
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="22" y1="2" x2="11" y2="13" />
                                <polygon points="22,2 15,22 11,13 2,9 22,2" />
                              </svg>
                            </button>
                          </div>
                          {showEmojiPicker && (
                            <div className="inline-vibe-emoji-picker">
                              <EmojiPicker
                                onEmojiSelect={handleEmojiSelect}
                                onClose={() => setShowEmojiPicker(false)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {activeVibePanel === "commentLikes" && (
                      <div className="inline-vibe-panel">
                        <div className="inline-vibe-panel-header">
                          <h3>Comment likes</h3>
                        </div>
                        <div className="inline-vibe-panel-list">
                          {commentLikes.length > 0 ? (
                            commentLikes.map((like) => (
                              <div key={like._id || like.user?._id} className="inline-vibe-list-item">
                                <div className="inline-vibe-list-avatar">
                                  {like.user?.profileImage ? (
                                    <img src={`${config.apiUrl}${like.user.profileImage}`} alt={like.user.username} />
                                  ) : (
                                    <div className="inline-vibe-list-avatar-placeholder notranslate">
                                      {like.user?.username?.charAt(0)?.toUpperCase() || "U"}
                                    </div>
                                  )}
                                </div>
                                <div className="inline-vibe-list-meta">
                                  <span className="inline-vibe-list-title notranslate">{like.user?.username || "Unknown User"}</span>
                                  <span className="inline-vibe-list-subtitle">{formatVibeTimeAgo(like.likedAt)}</span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="inline-vibe-empty-state">No likes yet</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="inline-vibe-viewer-navigation">
                    <button
                      className="inline-vibe-nav-btn"
                      onClick={() => setSelectedVibeIndex((prev) => Math.max(0, prev - 1))}
                      disabled={selectedVibeIndex === 0}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
                    </button>
                    <span className="inline-vibe-counter">{selectedVibeIndex + 1} / {selectedVibeStatuses.length}</span>
                    <button
                      className="inline-vibe-nav-btn"
                      onClick={() => setSelectedVibeIndex((prev) => Math.min(selectedVibeStatuses.length - 1, prev + 1))}
                      disabled={selectedVibeIndex === selectedVibeStatuses.length - 1}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
                    </button>
                  </div>
                  {(canOpenPreviousVibeUser || canOpenNextVibeUser) && (
                    <div className="inline-vibe-user-switcher-wrap">
                      {canOpenPreviousVibeUser ? (
                        <button
                          className="inline-vibe-user-nav-btn"
                          onClick={() =>
                            openVibeViewerAtIndex(
                              previousNavigableVibeUser,
                              (previousNavigableVibeUser.statuses || []).length - 1
                            )
                          }
                          title="Open previous vibe"
                        >
                          Previous vibe
                        </button>
                      ) : (
                        <span />
                      )}

                      {canOpenNextVibeUser ? (
                        <button
                          className="inline-vibe-user-nav-btn"
                          onClick={() => openVibeViewerAtIndex(nextNavigableVibeUser, 0)}
                          title="Open next vibe"
                        >
                          Next vibe
                        </button>
                      ) : (
                        <span />
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === "contacts" && (
          <Contacts
            user={user}
            onContactClick={handleContactClick}
            onProfileClick={handleOpenUserProfile}
            refreshUsers={refreshUsers}
            refreshNotificationCount={fetchNotificationCount}
            socket={socket}
          />
        )}

        {activeTab === "status" && <Status user={user} socket={socket} />}

        {activeTab === "groups" && (
          <Groups
            user={user}
            socket={socket}
            onGroupSelect={async (group, updateCallback, removeFromList) => {
              // Navigate to groups tab with group query param
              navigate(`/groups?group=${group._id}`);
              
              // Store the update callback for later use
              setGroupUpdateCallback(() => updateCallback);
              setRemoveGroupFromListCallback(() => removeFromList);
              
              // Leave previous group room if switching groups
              if (selectedGroup && selectedGroup._id !== group._id && socket) {
                socket.emit('leaveGroup', selectedGroup._id);
              }
              
              // Join the new group room
              if (socket) {
                socket.emit('joinGroup', group._id);
              }
              
              // Set the selected group state
              setAiChatOpen(false);
              setTo(null); // Clear individual chat
              setSelectedGroup(group);
              setGroupReplyTo(null); // Clear group reply when switching groups
              setShowChat(true); // Show chat view on mobile

              // Mark messages as read for this group
              try {
                const response = await axios.post(
                  `${config.apiUrl}/api/groups/${group._id}/mark-read`,
                  {
                    userId: user.id,
                  }
                );
                //console.log("Messages marked as read:", response.data);

                // Clear notification count for this group immediately
                setGroupNotificationCounts((prev) => {
                  const newCounts = { ...prev };
                  const clearedCount = newCounts[group._id] || 0;
                  delete newCounts[group._id];

                  // Update total count
                  setGroupNotificationCount((prev) =>
                    Math.max(0, prev - clearedCount)
                  );

                  return newCounts;
                });

                // No need for additional API call - state is already updated correctly
              } catch (error) {
                console.error("Error marking messages as read:", error);
              }
            }}
            groupNotificationCounts={groupNotificationCounts}
            refreshGroupNotificationCount={fetchGroupNotificationCount}
          />
        )}

        {activeTab === "calls" && (
          <div className="tab-content">
            <div className="sidebar-header">
              <div className="app-header">
                <div className="app-header-top">
                  <h2 className="app-name notranslate">Calls</h2>
                </div>
              </div>
            </div>
            <div className="calls-content">
              {joinableGroupCalls.length > 0 && (
                <div className="ongoing-group-calls">
                  <h3 className="ongoing-group-calls-title">Ongoing</h3>
                  {joinableGroupCalls.map((call) => (
                    <OngoingGroupCallCard
                      key={call.groupCallId}
                      call={call}
                      joining={String(joiningOngoingCallId) === String(call.groupCallId)}
                      onJoin={() => {
                        if (joiningOngoingCallId) return;
                        setJoiningOngoingCallId(call.groupCallId);
                        window.dispatchEvent(new CustomEvent('requestJoinGroupCall', { detail: call }));
                      }}
                    />
                  ))}
                </div>
              )}
              {callHistory.length === 0 && joinableGroupCalls.length === 0 ? (
                <div className="empty-calls">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    style={{ opacity: 0.3, margin: "20px auto" }}
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                  <p
                    style={{
                      textAlign: "center",
                      color: "#666",
                      padding: "0 20px",
                    }}
                  >
                    Your call history will appear here
                  </p>
                </div>
              ) : (
                <div className="call-history-list">
                  {callHistory.map((call, index) => {
                    const callUser = users.find((u) => u.id === call.userId);
                    const callDate = new Date(call.timestamp);
                    const isToday =
                      new Date().toDateString() === callDate.toDateString();
                    const isYesterday =
                      new Date(Date.now() - 86400000).toDateString() ===
                      callDate.toDateString();

                    let dateLabel;
                    if (isToday) {
                      dateLabel = callDate.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      });
                    } else if (isYesterday) {
                      dateLabel =
                        "Yesterday " +
                        callDate.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        });
                    } else {
                      dateLabel =
                        callDate.toLocaleDateString([], {
                          month: "short",
                          day: "numeric",
                        }) +
                        " " +
                        callDate.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        });
                    }

                    return (
                      <div
                        key={index}
                        className="call-history-item"
                        onClick={() => {
                          handleContactClick(call.userId);
                        }}
                      >
                        <div className="call-avatar">
                          {callUser?.profileImage ? (
                            <img
                              src={`${config.apiUrl}${callUser.profileImage}`}
                              alt={call.userName}
                            />
                          ) : (
                            <div className="call-avatar-placeholder notranslate">
                              {call.userName.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="call-info">
                          <div className="call-user-name notranslate">
                            {call.userName}
                          </div>
                          <div className="call-type-label">
                            {call.type === "video" ? "Video call" : "Audio call"}
                          </div>
                        </div>
                        <div className="call-time">{dateLabel}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "settings" && (
          <Settings
            user={user}
            onBack={() => navigate("/chats")}
            onUserUpdate={(updatedUser) => {
              // Update user in parent component (App.js) if possible
              
              // For now, we'll update localStorage and the user will be refreshed on next load
              localStorage.setItem("chatapp_user", JSON.stringify(updatedUser));
            }}
          />
        )}

        {activeTab === "starred-messages" && (
          <StarredMessages
            user={user}
            users={users}
            onBack={() => navigate("/chats")}
          />
        )}

        {activeTab === "feedback" && (
          <ContactUs
            currentUser={user}
            onBack={() => navigate("/chats")}
          />
        )}

        {activeTab === "pricing" && <Pricing user={user} />}
      </div>

      {/* Chat Area - Always visible on desktop, conditionally on mobile */}
      <div
        className={`main ${isMobile && !showChat ? "hidden" : ""} ${isWidePanelTab ? "hidden" : ""} ${isCurrentChatLockedAndSealed ? "main--chat-locked-sealed" : ""} ${!isCurrentChatLockedAndSealed && (getCurrentChatWallpaper().isCustom || getCurrentChatWallpaper().isPattern) ? "custom-wallpaper" : ""
          }`}
        style={
          aiChatOpen
            ? { background: "#f0f2f5" }
            : isCurrentChatLockedAndSealed
            ? { background: "#f1f5f9" }
            : {
          background: (getCurrentChatWallpaper().isCustom || getCurrentChatWallpaper().isPattern)
            ? `url(${getCurrentChatWallpaper().url})`
            : getCurrentChatWallpaper().url,
          backgroundSize:
            getCurrentChatWallpaper().displaySettings.backgroundSize,
          backgroundPosition:
            getCurrentChatWallpaper().displaySettings.backgroundPosition,
          backgroundRepeat:
            getCurrentChatWallpaper().displaySettings.backgroundRepeat,
          backgroundAttachment: "fixed",
        }}
      >
        {/* UCMA AI replaces the chat column content (same layout as a normal chat) */}
        {aiChatOpen ? (
          <AIChat
            user={user}
            isMobile={isMobile}
            onClose={() => {
              setAiChatOpen(false);
              if (isMobile) {
                setShowChat(false);
              }
            }}
          />
        ) : (
        <>
        <div className="header">
          {to ? (
            isCurrentChatLockedAndSealed ? (
            <div className="chat-header chat-header--locked-sealed">
              {isMobile && (
                <button className="back-button" onClick={handleBackToContacts}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M19 12H5" />
                    <path d="M12 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div className="chat-header-locked-title">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                <span>Locked chat</span>
              </div>
            </div>
            ) : (
            <div className="chat-header">
              {isMobile && (
                <button className="back-button" onClick={handleBackToContacts}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M19 12H5" />
                    <path d="M12 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div className="chat-user-avatar">
                {(() => {
                  const selectedUser = users.find((u) => u.id === to);
                  // Use privacy-filtered profile data if available; backend returns null for hidden photo
                  const privacyProfileImage =
                    selectedUserProfileData?.profileImage || null;
                  if (privacyProfileImage) {
                    const loadingStatus = profileImageLoading[to];
                    const isErrored = loadingStatus === 'error';
                    const isLoading = loadingStatus !== false && !isErrored;
                    return (
                      <div className="avatar-image-wrapper">
                        {isLoading && (
                          <div className="avatar-loading-spinner" aria-label="Loading avatar" />
                        )}
                        {!isErrored ? (
                          <img
                            src={`${config.apiUrl}${privacyProfileImage}`}
                            alt={selectedUser?.username}
                            className={`chat-profile-image ${isLoading ? 'loading' : 'loaded'}`}
                            onLoad={() => setProfileImageLoading(prev => ({ ...prev, [to]: false }))}
                            onError={() => setProfileImageLoading(prev => ({ ...prev, [to]: 'error' }))}
                          />
                        ) : (
                          <div className="chat-avatar-placeholder notranslate">
                            {selectedUser?.username?.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return (
                    <div className="chat-avatar-placeholder notranslate">
                      {selectedUser?.username?.charAt(0).toUpperCase()}
                    </div>
                  );
                })()}
              </div>
              <div
                className="chat-user-info"
                onClick={() => handleOpenUserProfile(to)}
              >
                <div className="chat-user-name clickable notranslate">
                  {users.find((u) => u.id === to)?.username || "Chat"}
                </div>
                <div className="chat-user-status">
                  {(() => {
                    // Show typing status if user is typing
                    if (to && typingUsers[to]) {
                      return (
                        <span className="typing-status">
                          typing...
                        </span>
                      );
                    }

                    // Otherwise show last seen status
                    const user = users.find((u) => u.id === to);
                    return formatLastSeen(
                      user?.lastSeen,
                      user?.lastSeen != null
                    );
                  })()}
                </div>
              </div>
              <div className="chat-actions">
                {/* These are thin trigger buttons. The actual AudioCall /
                    VideoCall components are mounted ONCE globally in App.js
                    (so socket listeners are not duplicated, which was causing
                    the "incoming call shown twice" bug). When clicked, we
                    publish a request that the global instance picks up. */}
                {(() => {
                  const targetUser = users.find((u) => u.id === to);
                  const disabled = !targetUser;
                  const startAudio = () => {
                    if (!targetUser) return;
                    window.dispatchEvent(new CustomEvent('requestStartAudioCall', { detail: { user: targetUser } }));
                  };
                  const startVideo = () => {
                    if (!targetUser) return;
                    window.dispatchEvent(new CustomEvent('requestStartVideoCall', { detail: { user: targetUser } }));
                  };
                  return (
                    <>
                      <span
                        className={`action-icon${disabled ? ' disabled' : ''}`}
                        onClick={disabled ? undefined : startAudio}
                        title={disabled ? 'Select a user to call' : 'Start audio call'}
                        role="button"
                        aria-label="Start audio call"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !disabled) startAudio(); }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                        </svg>
                      </span>
                      <span
                        className={`action-icon${disabled ? ' disabled' : ''}`}
                        onClick={disabled ? undefined : startVideo}
                        title={disabled ? 'Select a user to call' : 'Start video call'}
                        role="button"
                        aria-label="Start video call"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !disabled) startVideo(); }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polygon points="23 7 16 12 23 17 23 7"/>
                          <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                        </svg>
                      </span>
                    </>
                  );
                })()}
                {/* <span
                  className="action-icon"
                  onClick={() => setShowChatSearch(!showChatSearch)}
                  title="Search in this chat"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                </span> */}
                <span
                  className="action-icon chat-menu-trigger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChatMenuClick(e, to);
                  }}
                  title="More options"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="1" />
                    <circle cx="12" cy="5" r="1" />
                    <circle cx="12" cy="19" r="1" />
                  </svg>
                </span>
              </div>
            </div>
            )
          ) : selectedGroup ? (
            <div className="chat-header">
              {isMobile && (
                <button
                  className="back-button"
                  onClick={() => {
                    setSelectedGroup(null);
                    setShowChat(false);
                    navigate('/groups');
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M19 12H5" />
                    <path d="M12 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div
                className={`chat-user-avatar${selectedGroup.avatar ? " editable" : ""}`}
                onClick={handleGroupAvatarClick}
                title={selectedGroup.avatar ? "Click to view full image" : undefined}
              >
                {selectedGroup.avatar ? (
                  <img
                    src={`${config.apiUrl}${selectedGroup.avatar}`}
                    alt={selectedGroup.name}
                    className="chat-profile-image notranslate"
                  />
                ) : (
                  <div className="chat-avatar-placeholder">
                    {selectedGroup.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="chat-user-info">
                <div
                  className={`chat-user-name notranslate${Array.isArray(selectedGroup.members) && selectedGroup.members.length > 0
                    ? " clickable"
                    : ""
                    }`}
                  onClick={() => {
                    if (Array.isArray(selectedGroup.members) && selectedGroup.members.length > 0) {
                      setShowGroupMembersPage(true);
                    }
                  }}
                  title={
                    Array.isArray(selectedGroup.members) && selectedGroup.members.length > 0
                      ? "Click to view group members"
                      : "Members list unavailable"
                  }
                  style={{ cursor: Array.isArray(selectedGroup.members) && selectedGroup.members.length > 0 ? "pointer" : "default", userSelect: "none" }}
                >
                  {selectedGroup.name}
                </div>
                {selectedGroup.description && (
                  <div className="chat-user-description notranslate">
                    {selectedGroup.description}
                  </div>
                )}
                <div className="chat-user-status">
                  {(() => {
                    // Show typing status if someone is typing in the group
                    if (selectedGroup && groupTypingUsers[selectedGroup._id]) {
                      const typingUsersList = Object.values(groupTypingUsers[selectedGroup._id]);
                      if (typingUsersList.length === 1) {
                        return (
                          <span className="typing-status">
                            {typingUsersList[0].username} is typing...
                          </span>
                        );
                      } else if (typingUsersList.length > 1) {
                        return (
                          <span className="typing-status">
                            {typingUsersList.length} people are typing...
                          </span>
                        );
                      }
                    }

                    // Otherwise show empty (member count removed)
                    return '';
                  })()}
                </div>
              </div>
              <div className="chat-actions">
                {/* Launchers only. Incoming calls are handled by the hidden
                    global instances mounted at the bottom of this file, so
                    these must not react to incoming calls as well. */}
                <GroupAudioCall
                  user={user}
                  selectedGroup={selectedGroup}
                  onEndCall={handleCallEnd}
                  socket={socket}
                  ignoreIncoming={true}
                  joinRequest={groupCallJoinRequest}
                  onJoinRequestHandled={() => {
                    setGroupCallJoinRequest(null);
                    setJoiningGroupCall(false);
                  }}
                />
                <GroupVideoCall
                  user={user}
                  selectedGroup={selectedGroup}
                  onEndCall={handleCallEnd}
                  socket={socket}
                  ignoreIncoming={true}
                  joinRequest={groupCallJoinRequest}
                  onJoinRequestHandled={() => {
                    setGroupCallJoinRequest(null);
                    setJoiningGroupCall(false);
                  }}
                />
                <span
                  className="action-icon chat-menu-trigger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGroupMenuClick(e, selectedGroup._id);
                  }}
                  title="More options"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="1" />
                    <circle cx="12" cy="5" r="1" />
                    <circle cx="12" cy="19" r="1" />
                  </svg>
                </span>
              </div>
            </div>
          ) : (
            !isMobile && "Select a contact to start messaging"
          )}
        </div>

        <div className="main-chat-panel">
        {selectedGroup &&
          activeGroupCall &&
          !joiningGroupCall &&
          !isCurrentChatLockedAndSealed &&
          !(activeGroupCall.participantIds || []).some(
            (id) => String(id) === String(user?.id)
          ) && (
            <GroupCallJoinBar
              call={activeGroupCall}
              joining={joiningGroupCall}
              onJoin={() => {
                if (joiningGroupCall || !activeGroupCall) return;
                setJoiningGroupCall(true);
                setGroupCallJoinRequest(activeGroupCall);
              }}
            />
          )}
        {(to || selectedGroup) && !isCurrentChatLockedAndSealed && (
          <PinnedMessagesBar
            pinnedMessages={pinnedMessages.map((item) => {
              if (item.pinnedByName) return item;
              if (String(item.pinnedBy) === String(user?.id)) {
                return { ...item, pinnedByName: user?.username || "" };
              }
              const other = (users || []).find(
                (u) => String(u.id || u._id) === String(item.pinnedBy)
              );
              return { ...item, pinnedByName: other?.username || "" };
            })}
            currentUserId={user?.id}
            onSelect={scrollToPinnedMessage}
            onUnpin={(messageId) => handlePinMessage(messageId, false)}
            canUnpinPin={(item) =>
              selectedGroup
                ? canPinMessages
                : String(item?.pinnedBy) === String(user?.id)
            }
          />
        )}
        {to && isCurrentChatLockedAndSealed && (
          <div className="chat-lock-screen">
            <ChatLockUnlockPanel
              pinResetKey={chatLockPinFailCount}
              onUnlock={handleChatLockUnlock}
              onCancel={isMobile ? handleBackToContacts : undefined}
              onForgotPin={() => {
                setChatLockError("");
                setShowChatLockPinReset(true);
              }}
              error={chatLockError}
            />
          </div>
        )}

        {/* Chat Search Bar */}
        {showChatSearch && to && !isCurrentChatLockedAndSealed && (
          <div className="chat-search-bar">
            <div className="chat-search-input-wrapper">
              <span className="chat-search-icon">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Search messages in this chat..."
                value={chatSearchTerm}
                onChange={(e) => setChatSearchTerm(e.target.value)}
                className="chat-search-input"
                autoFocus
              />
              {chatSearchTerm && searchMatches.length > 0 && (
                <div className="chat-search-navigation">
                  <span className="search-match-count">
                    {searchMatchIndex + 1} of {searchMatches.length}
                  </span>
                  <button
                    className="search-nav-btn"
                    onClick={goToPreviousMatch}
                    title="Previous match"
                    disabled={searchMatches.length === 0}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  </button>
                  <button
                    className="search-nav-btn"
                    onClick={goToNextMatch}
                    title="Next match"
                    disabled={searchMatches.length === 0}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                </div>
              )}
              <button
                className="chat-search-close"
                onClick={() => {
                  setShowChatSearch(false);
                  setChatSearchTerm("");
                  setSearchMatches([]);
                  setSearchMatchIndex(0);
                }}
                title="Close search"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </button>
            </div>
            {chatSearchTerm && (
              <div className="chat-search-results-info">
                {displayMessages.length} message
                {displayMessages.length !== 1 ? "s" : ""} found
              </div>
            )}
          </div>
        )}

        <div 
          className="chat-window"
          ref={chatWindowRef}
          onClick={() => {
            if (showChatSearch) {
              setShowChatSearch(false);
              setChatSearchTerm("");
              setSearchMatches([]);
              setSearchMatchIndex(0);
            }
          }}
        >
          {to ? (
            isCurrentChatLockedAndSealed ? (
              <div className="chat-lock-empty-hint">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                <p>Messages are hidden until you unlock this chat.</p>
              </div>
            ) : (
            <>
              {/* Block Status Messages */}
              {blockStatus?.isToBlockedByFrom && (
                <div className="block-status-message">
                  <div className="block-status-content">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M4.93 4.93l14.14 14.14" />
                    </svg>
                    <span>
                      You blocked this contact. Messages will not be delivered.
                    </span>
                  </div>
                </div>
              )}
              {blockStatus?.isFromBlockedByTo && (
                <div className="block-status-message blocked-by">
                  <div className="block-status-content">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <span>You have been blocked by this contact.</span>
                  </div>
                </div>
              )}

              {/* Messages */}
              {labels.map((label) => (
                <React.Fragment key={label}>
                  <div className="date-divider">{label}</div>
                  {grouped[label].map((msg, i) => {
                    const isMe = msg.from === user.id;
                    const tick = getTick(msg, messages, user.id, user, timeUpdateTrigger);

                    // WhatsApp-style in-chat call log entry (additive feature).
                    if (msg.messageType === "call" || msg.type === "call") {
                      const cd = msg.callData || {};
                      const isVideoCall = cd.callType === "video";
                      const status = cd.callStatus || "missed";
                      const answered = status === "answered";
                      const outgoing = msg.from === user.id;
                      const notAnswered = !answered;
                      let subtitle;
                      if (answered) {
                        subtitle = formatCallDuration(cd.duration);
                      } else if (outgoing) {
                        subtitle = "No answer";
                      } else {
                        subtitle = status === "rejected" ? "Declined" : "Missed call";
                      }
                      const otherId = isMe ? msg.to : msg.from;
                      const callBack = () => {
                        const targetUser = users.find((u) => u.id === otherId);
                        if (!targetUser) return;
                        window.dispatchEvent(
                          new CustomEvent(
                            isVideoCall ? "requestStartVideoCall" : "requestStartAudioCall",
                            { detail: { user: targetUser } }
                          )
                        );
                      };
                      return (
                        <div
                          key={msg._id || i}
                          data-message-id={msg._id}
                          className={`message ${isMe ? "me" : "them"} call-log-message`}
                          onContextMenu={(e) => handleMessageContextMenu(e, msg)}
                        >
                          <div
                            className={`call-log-bubble ${outgoing ? "outgoing" : "incoming"} ${notAnswered ? "call-missed" : "call-answered"}`}
                            onClick={callBack}
                            role="button"
                            title="Call back"
                          >
                            <span className="call-log-icon">
                              {isVideoCall ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M23 7l-7 5 7 5V7z" />
                                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                </svg>
                              ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.36 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.34 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                              )}
                            </span>
                            <div className="call-log-info">
                              <span className="call-log-title">
                                <span className={`call-log-arrow ${outgoing ? "out" : "in"}`}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    {outgoing ? (
                                      <>
                                        <line x1="7" y1="17" x2="17" y2="7" />
                                        <polyline points="7 7 17 7 17 17" />
                                      </>
                                    ) : (
                                      <>
                                        <line x1="17" y1="7" x2="7" y2="17" />
                                        <polyline points="17 17 7 17 7 7" />
                                      </>
                                    )}
                                  </svg>
                                </span>
                                {isVideoCall ? "Video call" : "Voice call"}
                              </span>
                              <span className="call-log-sub">{subtitle}</span>
                            </div>
                            <span className="call-log-time">{formatTime(msg.createdAt)}</span>
                          </div>
                        </div>
                      );
                    }

                    // Debug: Log message structure
                    if (i === 0) {
                      // Only log first message of each group to avoid spam
                      // //console.log("Rendering message:", {
                      //   messageId: msg._id,
                      //   hasId: !!msg._id,
                      //   from: msg.from,
                      //   to: msg.to,
                      //   text: msg.text?.substring(0, 50), // First 50 chars
                      // });
                    }

                    return (
                      <div
                        key={msg._id || i}
                        data-message-id={msg._id}
                        className={`message ${isMe ? "me" : "them"} ${msg.messageType === "voice" ? "voice" : ""
                          }`}
                        onDoubleClick={() => setReplyTo(msg)}
                        onContextMenu={(e) => handleMessageContextMenu(e, msg)}
                        onTouchStart={(e) => {
                          // Handle long press on mobile
                          const timer = setTimeout(() => {
                            handleMessageContextMenu(e, msg);
                          }, 500);
                          const handleTouchEnd = () => {
                            clearTimeout(timer);
                            document.removeEventListener(
                              "touchend",
                              handleTouchEnd
                            );
                          };
                          document.addEventListener("touchend", handleTouchEnd);
                        }}
                      >
                        {msg.forwarded && (
                          <div className="forwarded-label">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="15 17 20 12 15 7" />
                              <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
                            </svg>
                            Forwarded
                          </div>
                        )}
                        {msg.replyTo && (
                          <div className="reply-preview-in-msg">
                            <span className="reply-author">
                              {msg.replyTo.from === user.id
                                ? "You"
                                : users.find((u) => u.id === msg.replyTo.from)
                                  ?.username || "User"}
                            </span>
                            <span className="reply-text">
                              {msg.replyTo.messageType === "image" ||
                              msg.replyTo.type === "image"
                                ? "📷 Image"
                                : msg.replyTo.messageType === "video" ||
                                  msg.replyTo.type === "video"
                                  ? "🎥 Video"
                                  : msg.replyTo.messageType === "document" ||
                                    msg.replyTo.type === "document"
                                    ? "📄 Document"
                                    : msg.replyTo.messageType === "audio" ||
                                      msg.replyTo.type === "audio"
                                      ? "🎵 Audio"
                                      : msg.replyTo.messageType === "voice" ||
                                        msg.replyTo.type === "voice"
                                        ? "🎤 Voice message"
                                        : msg.replyTo.messageType === "contact" ||
                                          msg.replyTo.type === "contact"
                                          ? "👤 Contact"
                                          : msg.replyTo.messageType === "location" ||
                                            msg.replyTo.type === "location"
                                            ? "📍 Location"
                                            : msg.replyTo.messageType === "call" ||
                                              msg.replyTo.type === "call"
                                              ? "📞 Call"
                                              : msg.replyTo.text}
                            </span>
                          </div>
                        )}
                        {msg.messageType === "voice" ? (
                          <VoiceMessage message={msg} isMe={isMe} />
                        ) : msg.viewOnce &&
                          (msg.messageType === "image" ||
                            msg.type === "image" ||
                            msg.messageType === "video" ||
                            msg.type === "video" ||
                            msg.messageType === "audio" ||
                            msg.type === "audio") ? (
                          <ViewOnceMedia
                            message={msg}
                            currentUserId={user?.id}
                            isSender={isMe}
                            endpoint={`${config.apiUrl}/api/messages/${msg._id}/view-once-media?userId=${user.id}`}
                            onViewed={(messageId) => {
                              setMessages((prev) =>
                                (Array.isArray(prev) ? prev : []).map((item) =>
                                  String(item._id) === String(messageId)
                                    ? {
                                        ...item,
                                        viewOnceViewed: true,
                                        viewedBy: [
                                          ...(item.viewedBy || []),
                                          { userId: user.id, viewedAt: new Date() },
                                        ],
                                      }
                                    : item
                                )
                              );
                            }}
                          />
                        ) : msg.messageType === "image" ||
                          msg.type === "image" ? (
                          <div className="msg-image">
                            <img
                              src={getImageUrl(msg.imageUrl, config.apiUrl)}
                              alt="Shared image"
                              className="chat-image"
                              onClick={() => handleImageClick(msg.imageUrl)}
                            />
                          </div>
                        ) : msg.messageType === "video" ||
                          msg.type === "video" ? (
                          <div className="msg-video">
                            <video
                              src={getMediaUrl(msg.videoUrl, config.apiUrl)}
                              className="chat-video"
                              controls
                              preload="metadata"
                            >
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        ) : msg.messageType === "audio" ||
                          msg.type === "audio" ? (
                          <div className="msg-audio">
                            <div className="audio-info">
                              <div className="audio-icon">
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"
                                    fill="#e74c3c"
                                  />
                                  <path
                                    d="M19 10v2a7 7 0 01-14 0v-2"
                                    stroke="#e74c3c"
                                    strokeWidth="2"
                                    fill="none"
                                  />
                                  <line
                                    x1="12"
                                    y1="19"
                                    x2="12"
                                    y2="23"
                                    stroke="#e74c3c"
                                    strokeWidth="2"
                                  />
                                  <line
                                    x1="8"
                                    y1="23"
                                    x2="16"
                                    y2="23"
                                    stroke="#e74c3c"
                                    strokeWidth="2"
                                  />
                                </svg>
                              </div>
                              <div className="audio-details">
                                <span className="audio-name">
                                  {msg.audioName}
                                </span>
                                <span className="audio-size">
                                  {formatFileSize(msg.audioSize)}
                                </span>
                              </div>
                            </div>
                            <audio
                              src={getMediaUrl(msg.audioUrl, config.apiUrl)}
                              className="chat-audio"
                              controls
                              preload="metadata"
                            >
                              Your browser does not support the audio element.
                            </audio>
                          </div>
                        ) : msg.messageType === "document" ||
                          msg.type === "document" ? (
                          <div className="msg-document">
                            <div className="document-info">
                              <div className="document-icon">
                                {getDocumentIcon(msg.documentType)}
                              </div>
                              <div className="document-details">
                                <span className="document-name">
                                  {msg.documentName}
                                </span>
                                <span className="document-size">
                                  {formatFileSize(msg.documentSize)}
                                </span>
                              </div>
                              <button
                                onClick={() =>
                                  handleDocumentDownload(
                                    msg.documentUrl,
                                    msg.documentName
                                  )
                                }
                                className="document-download-btn"
                                type="button"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                                  <polyline points="7,10 12,15 17,10" />
                                  <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ) : msg.messageType === "contact" ||
                          msg.type === "contact" ? (
                          <ContactMessage
                            contactData={msg.contactData}
                            currentUserId={user.id}
                            onContactClick={handleContactClick}
                            isOwn={isMe}
                          />
                        ) : msg.messageType === "location" ? (
                          <LocationMessage
                            locationData={msg.locationData}
                            isOwn={isMe}
                            timestamp={msg.createdAt}
                            senderName={
                              isMe
                                ? "You"
                                : users.find((u) => u.id === msg.from)
                                  ?.username || "User"
                            }
                            currentUserLocation={currentUserLocation}
                          />
                        ) : msg.messageType === "deleted" ? (
                          <span className="msg-text deleted">
                            {msg.text || "Sender deleted this message"}
                          </span>
                        ) : (
                          <>
                            <span className="msg-text notranslate">
                              {renderTextWithLinks(msg.text)}
                            </span>
                            {/* Link Previews - limit to first 3 URLs */}
                            {!user?.privacySettings?.disableLinkPreviews &&
                              extractUrls(msg.text)
                                .slice(0, 3)
                                .map((url, index) => (
                                  <LinkPreview
                                    key={`${msg._id}-${index}`}
                                    url={url}
                                    isOwn={isMe}
                                  />
                                ))}
                          </>
                        )}

                        <span className="msg-meta">
                          {msg.isEdited && <span className="edited-indicator">edited</span>}
                          {formatTime(msg.createdAt)}
                          {isMe && tick}
                          {/* Reactions summary */}
                          {msg.reactions && msg.reactions.length > 0 && (
                            <button
                              className="reactions-summary"
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openReactionDetails(msg);
                              }}
                              title="See who reacted"
                            >
                              {(() => {
                                const counts = msg.reactions.reduce(
                                  (acc, r) => {
                                    acc[r.type] = (acc[r.type] || 0) + 1;
                                    return acc;
                                  },
                                  {}
                                );
                                return Object.entries(counts).map(
                                  ([emoji, count]) => (
                                    <span key={emoji} className="reaction-chip">
                                      {emoji} {count}
                                    </span>
                                  )
                                );
                              })()}
                            </button>
                          )}
                          {pinnedMessageIds.includes(String(msg._id)) && (
                            <span className="pin-indicator" title="Pinned">📌</span>
                          )}
                          {msg.starredBy && msg.starredBy.includes(user.id) && (
                            <span className="star-indicator">
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                              </svg>
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </>
            )
          ) : selectedGroup ? (
            <>
              <GroupMessages
                group={selectedGroup}
                user={user}
                socket={socket}
                currentUserLocation={currentUserLocation}
                onImageClick={handleImageClick}
                onContactClick={handleContactClick}
                onReply={(message) => {
                  //console.log("Setting group reply to:", message);
                  //console.log("Message from object:", message.from);
                  setGroupReplyTo(message);
                }}
                onEdit={(message) => {
                  handleEditMessage(message);
                }}
                onMessageSent={(message) => {
                  // This will be handled by the socket listener
                  //console.log("Group message sent:", message);
                }}
                searchTerm={showChatSearch ? chatSearchTerm : ''}
                clearedAt={clearedGroups[selectedGroup._id]}
                pinnedMessageIds={pinnedMessageIds}
                canPin={canPinMessages}
                onPin={handlePinMessage}
              />
            </>
          ) : (
            <WelcomeScreen mini />
          )}
          {(to || selectedGroup) && (
            <div
              ref={messagesEndRef}
              className="chat-messages-end"
              aria-hidden="true"
            />
          )}
        </div>

        {/* Single MessageInput component for both normal chat and group chat */}
        {((to && !isCurrentChatLockedAndSealed) || selectedGroup) && (
          <MessageInput
            text={text}
            setText={setText}
            onSend={() => {
              if (!text.trim()) return;

              // If in edit mode, save the edit
              if (editMessage) {
                handleSaveEdit();
                return;
              }

              if (to) {
                // Normal chat
                handleSend();
              } else if (selectedGroup) {
                // Group chat
                const messageData = {
                  from: user.id,
                  groupId: selectedGroup._id,
                  text: text.trim(),
                  messageType: "text",
                  replyTo: groupReplyTo?._id || null,
                };
                socket.emit("sendGroupMessage", messageData);
                setText("");
                setGroupReplyTo(null); // Clear reply after sending
              }
            }}
            editMessage={editMessage}
            onCancelEdit={handleCancelEdit}
            onVoiceMessage={(audioFile, audioUrl) => {
              if (to) {
                // Normal chat
                handleVoiceMessage(audioFile, audioUrl);
                setTimeout(scrollToBottom, 50);
              } else if (selectedGroup) {
                // Group chat
                handleGroupVoiceMessage(audioFile, audioUrl, groupReplyTo);
                setTimeout(scrollToBottom, 50);
              }
            }}
            onMediaSend={(mediaFiles, options) => {
              if (to) {
                // Normal chat
                handleMediaSend(mediaFiles, options);
              } else if (selectedGroup) {
                // Group chat
                handleGroupMediaSend(mediaFiles, options);
              }
            }}
            onGifSend={handleGifSend}
            onDocumentSend={(documentFile) => {
              if (to) {
                // Normal chat
                handleDocumentSend(documentFile);
              } else if (selectedGroup) {
                // Group chat
                handleGroupDocumentSend(documentFile);
              }
            }}
            onAudioSend={(audioFile, options) => {
              if (to) {
                // Normal chat
                handleAudioSend(audioFile, options);
              } else if (selectedGroup) {
                // Group chat
                handleGroupAudioSend(audioFile, options);
              }
            }}
            replyTo={
              to && replyTo
                ? {
                  from:
                    replyTo.from === user.id
                      ? "me"
                      : users.find((u) => u.id === replyTo.from)?.username ||
                      "User",
                  text: replyTo.text,
                  messageType: replyTo.messageType || replyTo.type,
                }
                : selectedGroup && groupReplyTo
                  ? {
                    from:
                      groupReplyTo.from._id === user.id
                        ? "me"
                        : groupReplyTo.from.username ||
                        groupReplyTo.from.name ||
                        "User",
                    text: groupReplyTo.text,
                    messageType: groupReplyTo.messageType || groupReplyTo.type,
                  }
                  : null
            }
            onReplyClose={() => {
              if (to) setReplyTo(null);
              if (selectedGroup) setGroupReplyTo(null);
            }}
            user={user}
            onContactSend={to ? handleContactSend : handleGroupContactSend}
            onLocationSend={to ? handleLocationSend : handleGroupLocationSend}
            disabled={
              to
                ? isCurrentChatLockedAndSealed || !blockStatus?.canSendMessage
                : selectedGroup
                  ? Boolean(readOnlyGroups[selectedGroup._id]) && (!Array.isArray(selectedGroup.members) || selectedGroup.members.length === 0)
                  : false
            }
            blockMessage={
              to && isCurrentChatLockedAndSealed
                ? "Unlock this chat to send messages"
                : to && blockStatus?.isToBlockedByFrom
                ? "You have blocked this contact"
                : to && blockStatus?.isFromBlockedByTo
                  ? "You have been blocked by this contact"
                  : selectedGroup && readOnlyGroups[selectedGroup._id]
                    ? "You are no longer a member of this group"
                    : null
            }
            socket={socket}
            to={to}
            selectedGroup={selectedGroup}
          />
        )}
        </div>
        </>
        )}
      </div>

      {/* Message Context Menu */}
      <MessageContextMenu
        message={{
          ...contextMenu.message,
          isPinned: pinnedMessageIds.includes(String(contextMenu.message?._id)),
          pinnedBy: pinnedMessages.find(
            (item) => String(item.messageId) === String(contextMenu.message?._id)
          )?.pinnedBy,
        }}
        isVisible={contextMenu.isVisible}
        position={contextMenu.position}
        onClose={closeContextMenu}
        onDelete={handleDeleteMessage}
        onStar={handleStarMessage}
        onReply={handleReplyToMessage}
        onForward={handleForwardMessage}
        onEdit={handleEditMessage}
        onReact={handleReactToMessage}
        onPin={handlePinMessage}
        canPin={
          canPinMessages &&
          !pinnedMessageIds.includes(String(contextMenu.message?._id))
        }
        canUnpin={
          pinnedMessageIds.includes(String(contextMenu.message?._id)) &&
          String(
            pinnedMessages.find(
              (item) => String(item.messageId) === String(contextMenu.message?._id)
            )?.pinnedBy
          ) === String(user?.id)
        }
        isMe={contextMenu.message?.from === user?.id}
        currentUser={user}
      />

      <ForwardPicker
        isVisible={forwardModal.isVisible}
        user={user}
        onClose={closeForwardModal}
        onForward={handleForwardConfirm}
      />

      {reactionDetailsMessage && (
        <div className="reaction-details-overlay" onClick={closeReactionDetails}>
          <div
            className="reaction-details-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Message reactions"
          >
            <div className="reaction-details-header">
              <h4>Reactions</h4>
              <button
                type="button"
                className="reaction-details-close"
                onClick={closeReactionDetails}
                aria-label="Close reactions"
              >
                ×
              </button>
            </div>

            <div className="reaction-details-list">
              {(reactionDetailsMessage.reactions || []).map((reaction, index) => {
                const isOwnReaction =
                  getReactionUserId(reaction) === user?.id?.toString?.();
                return (
                  <div
                    className="reaction-details-item"
                    key={`${getReactionUserId(reaction) || "unknown"}-${reaction.type}-${index}`}
                  >
                    <span className="reaction-details-emoji">{reaction.type}</span>
                    <span className="reaction-details-name">
                      {getReactionUserDisplayName(reaction)}
                    </span>
                    {isOwnReaction && (
                      <button
                        type="button"
                        className="reaction-details-remove"
                        onClick={() =>
                          handleRemoveOwnReaction(reactionDetailsMessage)
                        }
                      >
                        Remove
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* User Profile Modal */}
      {showUserProfile && (
        <UserProfile
          user={user}
          selectedUserId={selectedUserForProfile}
          onClose={handleCloseUserProfile}
          onBlock={handleUserBlock}
          onUnblock={handleUserUnblock}
          socket={socket}
        />
      )}

      {showSharedContentModal && sharedContentContactId && (
        <SharedContentModal
          user={user}
          contactUserId={sharedContentContactId}
          contactName={
            users.find((u) => u.id === sharedContentContactId)?.username
          }
          onClose={handleCloseSharedContent}
        />
      )}

      {/* Fullscreen Image Viewer */}
      <ImageViewer
        isOpen={imageViewer.isOpen}
        imageUrl={imageViewer.imageUrl}
        onClose={handleCloseImageViewer}
      />

      {/* Wallpaper Picker */}
      <WallpaperPicker
        isOpen={showWallpaperPicker}
        onClose={() => setShowWallpaperPicker(false)}
        onSelectWallpaper={handleWallpaperSelect}
        currentWallpaper={getCurrentChatWallpaper()}
      />

      {/* Global Search Modal */}
      {showGlobalSearch && (
        <div className="global-search-overlay" onClick={() => setShowGlobalSearch(false)}>
          <div className="global-search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="global-search-header">
              <div className="global-search-input-wrapper">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="global-search-icon"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={globalSearchTerm}
                  onChange={(e) => setGlobalSearchTerm(e.target.value)}
                  className="global-search-input"
                  autoFocus
                />
                {globalSearchTerm && (
                  <button
                    className="global-search-clear"
                    onClick={() => setGlobalSearchTerm("")}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M18 6L6 18" />
                      <path d="M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <button
                className="global-search-close"
                onClick={() => setShowGlobalSearch(false)}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="global-search-results">
              {globalSearchTerm && globalSearchResults.length === 0 ? (
                <div className="global-search-empty">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  <p>No messages found</p>
                </div>
              ) : !globalSearchTerm ? (
                <div className="global-search-empty">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  <p>Search for messages across all chats</p>
                </div>
              ) : (
                globalSearchResults.map((result) => {
                  const otherUser = users.find(
                    (u) => u.id === (result.from === user.id ? result.to : result.from)
                  );
                  return (
                    <div
                      key={result._id}
                      className="global-search-result-item"
                      onClick={() => {
                        const targetUserId = otherUser?.id;
                        const isSameChat = to === targetUserId;
                        
                        // Close search modal
                        setShowGlobalSearch(false);
                        setGlobalSearchTerm("");
                        
                        // If different chat, switch to it first
                        if (!isSameChat) {
                          setTo(targetUserId);
                          setShowChat(true);
                        }
                        
                        // Scroll to message after a delay (longer if switching chats)
                        setTimeout(() => {
                          const messageElement = document.querySelector(
                            `[data-message-id="${result._id}"]`
                          );
                          if (messageElement) {
                            messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
                            messageElement.classList.add("highlight-flash");
                            setTimeout(() => {
                              messageElement.classList.remove("highlight-flash");
                            }, 2000);
                          }
                        }, isSameChat ? 100 : 500);
                      }}
                    >
                      <div className="search-result-avatar">
                        {otherUser?.profileImage ? (
                          <img
                            src={`${config.apiUrl}${otherUser.profileImage}`}
                            alt={otherUser.username}
                          />
                        ) : (
                          <div className="avatar-placeholder">
                            {otherUser?.username?.charAt(0).toUpperCase() || "?"}
                          </div>
                        )}
                      </div>
                      <div className="search-result-content">
                        <div className="search-result-header">
                          <span className="search-result-name notranslate">
                            {otherUser?.username || "Unknown User"}
                          </span>
                          <span className="search-result-time">
                            {formatTime(result.createdAt)}
                          </span>
                        </div>
                        <div className="search-result-message notranslate">
                          {result.from === user.id && (
                            <span className="search-result-you">You: </span>
                          )}
                          {highlightText(result.text, globalSearchTerm)}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Contact Menu */}
      {contactMenu.isVisible && (
        <div className="contact-menu-overlay" onClick={closeContactMenu}>
          <div
            className="contact-menu"
            style={{
              position: "fixed",
              left: contactMenu.position.x,
              top: contactMenu.position.y,
              zIndex: 1000,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="contact-menu-item"
              onClick={() => handleContactMenuAction("wallpaper")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21,15 16,10 5,21" />
              </svg>
              Change Wallpaper
            </div>
            <div
              className="contact-menu-item"
              onClick={() => handleContactMenuAction("search")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              Search Messages
            </div>
            <div
              className="contact-menu-item"
              onClick={() => handleContactMenuAction("sharedContent")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
              View shared content
            </div>
            <div
              className="contact-menu-item"
              onClick={() => handleContactMenuAction("viewContact")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              View Contact
            </div>
            <div
              className="contact-menu-item"
              onClick={() => handleContactMenuAction("clearHistory")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
                <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
              </svg>
              Clear History
            </div>
            {contactMenu.contactId && isChatLocked(lockedChats, contactMenu.contactId) ? (
              <div
                className="contact-menu-item"
                onClick={() => handleContactMenuAction("unlockChat")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Unlock chat
              </div>
            ) : (
              <div
                className="contact-menu-item"
                onClick={() => handleContactMenuAction("lockChat")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Lock chat
              </div>
            )}
            {contactMenu.contactId && blockedUsers[contactMenu.contactId] ? (
              <div
                className="contact-menu-item unblock"
                onClick={() => handleContactMenuAction("unblock")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M9 12l2 2 4-4" />
                  <circle cx="12" cy="12" r="10" />
                </svg>
                Unblock Contact
              </div>
            ) : (
              <div
                className="contact-menu-item danger"
                onClick={() => handleContactMenuAction("block")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M4.93 4.93l14.14 14.14" />
                </svg>
                Block Contact
              </div>
            )}
          </div>
        </div>
      )}

      {/* Group Menu */}
      {groupMenu.isVisible && (
        <div className="contact-menu-overlay" onClick={closeGroupMenu}>
          <div
            className="contact-menu"
            style={{
              position: "fixed",
              left: groupMenu.position.x,
              top: groupMenu.position.y,
              zIndex: 1000,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="contact-menu-item"
              onClick={() => handleGroupMenuAction("wallpaper")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21,15 16,10 5,21" />
              </svg>
              Change Wallpaper
            </div>
            <div
              className="contact-menu-item"
              onClick={() => handleGroupMenuAction("search")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              Search Messages
            </div>
            <div
              className="contact-menu-item"
              onClick={() => handleGroupMenuAction("viewGroup")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              View Group
            </div>
            <div
              className="contact-menu-item"
              onClick={() => handleGroupMenuAction("clearHistory")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
                <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
              </svg>
              Clear History
            </div>
            <div
              className={`contact-menu-item danger ${readOnlyGroups[groupMenu.groupId] ? 'disabled' : ''}`}
              onClick={() => {
                if (!readOnlyGroups[groupMenu.groupId]) {
                  handleGroupMenuAction("leaveGroup");
                }
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M10 17l-5-5 5-5" />
                <path d="M19 12H5" />
              </svg>
              Leave Group
            </div>
          </div>
        </div>
      )}

      {renderLeaveGroupModals()}

      {showChatLockPinReset && (
        <ChatLockPinResetModal
          user={user}
          onCancel={() => setShowChatLockPinReset(false)}
          onSuccess={handleChatLockPinResetSuccess}
        />
      )}

      {chatLockModal.isVisible && (
        <ChatLockModal
          mode={chatLockModal.mode}
          contactName={
            chatLockModal.pendingContactId
              ? users.find((u) => u.id === chatLockModal.pendingContactId)?.username
              : undefined
          }
          onCancel={closeChatLockModal}
          onSubmit={handleChatLockModalSubmit}
          error={chatLockError}
        />
      )}

      {/* Confirmation Modal — portal so it stacks above vibe viewer on desktop */}
      {confirmModal.isVisible &&
        createPortal(
          <div className="confirm-modal-overlay" onClick={hideConfirmModal}>
            <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="confirm-modal-header">
                <h3>{confirmModal.title}</h3>
              </div>
              <div className="confirm-modal-body">
                <p>{confirmModal.message}</p>
              </div>
              <div className="confirm-modal-footer">
                <button
                  className="confirm-modal-cancel"
                  onClick={hideConfirmModal}
                >
                  {confirmModal.cancelText}
                </button>
                <button
                  className="confirm-modal-confirm"
                  onClick={handleConfirmation}
                >
                  {confirmModal.confirmText}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <VibeUploadModal
        isOpen={showVibeUploadModal}
        onClose={() => setShowVibeUploadModal(false)}
        user={user}
        socket={socket}
        onSuccess={fetchVibeUsers}
        variant="chats"
        initialType="text"
      />

    </div>
  );
}

export default Home;
